/* DRIVE IQ ADVENTURE SEARCH V600.22: wedding venue intent + county geocoder support. Voice V602.5 county handoff still requires validation. */
/* DRIVE IQ — ADVENTURE MODE V600.21 VOICE CITY LOOKUP GUARD (V600.20 PRESERVED)
   File: adventure-search.js

   V600.20 focused repair:
   - Expands generic shopping intent into provider-safe retail families.
   - Prevents “best shopping places” from becoming one overly literal query.
   - Adds malls, shopping centers, outlets, boutiques, department stores,
     retail centers, markets, antiques, and local shops as shopping recall signals.
   - Preserves destination resolution, city-first Voice IQ, and all UI behavior.

   V600.19 focused repair:
   - Uses whole-phrase intent matching so “barbecue” cannot falsely trigger “bar” / Nightlife.
   - Preserves BBQ as the primary category for requests such as “best barbecue in Memphis.”
   - Applies the same token-boundary protection to preferences, attributes, cuisines, shopping, and exclusions.

   V600.19 focused upgrade:
   - Expands natural-language intent for food, nightlife, live music, shopping,
     coffee, dessert, family activities, outdoors, scenic, and waterfront requests.
   - Preserves combined intent such as Nightlife + Live Music instead of discarding
     the secondary category.
   - Adds cuisine, shopping type, product, attribute, price, and exclusion signals.
   - Understands real-world modifiers including local only, not a chain, not a club,
     affordable, upscale, quiet, lively, walkable, easy parking, and worth the drive.
   - Keeps canonical destination authority and all existing autocomplete behavior intact.
   - Produces provider-safe recommendation queries for typed search and future Voice IQ.

   V600.17 focused repair:
   - Adds Charleston, South Carolina as a first-class canonical destination city.
   - Makes a bare Charleston search resolve to Charleston, SC instead of an ambiguous
     provider locality, street, business, or Charleston, West Virginia result.
   - Preserves exact Charleston, West Virginia searches when the state is explicit.
   - Adds Historic Charleston, French Quarter, King Street, and Waterfront Park as
     Explore Within Charleston destination children with exact centers and radii.
   - Carries Charleston destination DNA, scan presets, market label, bounds, and
     canonical identity through Search IQ to Scan IQ.
   - Keeps all existing Geoapify autocomplete, city authority, locking, and stale
     response protections unchanged.

   Fix:
   - Adds real Geoapify destination autocomplete.
   - Renders one stable vertical destination search list.
   - Canonical cities such as Destin, Florida rank above streets and districts.
   - Selecting a result emits search:chosen for the V600 destination controller.
   - Locks suggestions after destination selection and during scan/results modes.
   - Invalidates pending debounce work and late Geoapify responses when locked.
   - Prevents stale city/postcode cards from repainting over Adventure results.
   - Unlocks only when the user explicitly focuses the destination input.
   - Exposes close/lock/unlock/status APIs for UI authority integration.
   - Makes the vertical V600 search list the only destination-search UI.
   - Permanently retires legacy floating and horizontal destination choosers.
   - Re-suppresses legacy chooser panels during focus, typing, results, and mode changes.
   - Uses #diqAdventureSearchList as the only destination-search container.
   - Permanently separates destination search from #diqV600ResultShelf.
   - Removes stale #diqAdventureSearchList nodes on boot.
   - Removes DOM text scanning and recurring legacy chooser heartbeat logic.
   - Keeps Geoapify ranking, selection, locking, and late-response protection.
   - Understands natural searches such as coffee near me and live music nearby.
   - Treats near me, nearby, close by, and around me as current-location intent.
   - Defaults bare category searches such as coffee or pizza to Near Me.
   - Preserves category and preference intent when a city or district is explicit.
   - Searches only the explicit destination portion of phrases such as coffee in Chicago.
   - Exposes parsed intent for Adventure Controls and future Voice IQ handoff.
   - Prevents stripped Near Me searches from handing the raw phrase "coffee near me" to the place provider.
   - Supplies a clean canonical recommendation query such as "coffee" or "live music" after location language is removed.
   - Adds Destination Entity IQ before generic autocomplete for cities, districts,
     corridors, lakes, beaches, scenic areas, and major adventure destinations.
   - Resolves 30A as the Scenic Highway 30A / South Walton corridor instead of
     businesses whose names merely contain 30A.
   - Resolves Pickwick Lake as a broad lake recreation market rather than one pin.
   - Preserves canonical entity type, center, bounds, aliases, exploration radius,
     market label, and destination-cluster metadata.
   - Makes canonical entities outrank literal-name businesses and distant matches.
   - Applies geographic truth so a Fort Lauderdale business cannot outrank the
     actual 30A corridor for a destination-intent search.
   - Deduplicates repeated autocomplete results by place identity and coordinates.
   - Caps the search panel above the bottom CTA and keyboard safe area.
   - Keeps the result list internally scrollable while preserving a stable panel.
   - Tightens result-card spacing and reduces visual overlap on smaller phones.
   - Smoothly adapts panel height while the on-screen keyboard is open.
   - Fits the destination chooser to its actual result content so short lists
     never leave a large empty menu gap.
   - Uses compact, medium, and scrollable panel modes based on result count.
   - Keeps the Adventure IQ heading and subtitle visible above the chooser.
   - Gives canonical destinations a richer destination-first presentation.
   - Adds destination preview language for corridors, lakes, and districts.
   - Suppresses provider duplicates that represent the same canonical entity.
   - Preserves internal scrolling only when the result list genuinely needs it.
   - Adds canonical city authority so exact city searches resolve the city first.
   - Adds Destin, Florida as a first-class destination entity.
   - Keeps HarborWalk Village directly beneath Destin as an Explore Within district.
   - Applies City → District → Place ordering for exact destination searches.
   - Prevents a district from replacing its parent city.
   - Heavily penalizes unrelated same-name places outside the active market.
   - Labels recognized child districts as Explore Within <City>.
   - Injects the strongest registered child districts beneath an exact city result.
   - Keeps the canonical city first while preserving district shortcuts.
   - Limits initial district shortcuts to a small, useful set.
   - Preserves each district's own center, radius, bounds, and scan identity.
   - Allows child districts to survive canonical duplicate suppression.
   - Orders results as City → Child Districts → Nearby Destination Entities → Places.
   - Adds Destination Profile IQ so each canonical destination knows why people visit.
   - Preserves destination DNA, signature experiences, preferred categories,
     time-of-day priorities, exclusions, and destination themes.
   - Gives exact canonical entities priority over generic category parsing when the
     full query is a known destination such as Pickwick Lake or 30A.
   - Prevents words like lake, beach, park, or harbor inside a canonical destination
     name from being misclassified as a generic Outdoors search.
   - Suppresses irrelevant provider autocomplete beneath exact canonical entities.
   - Carries destination profiles through search selection for Scan IQ ranking.
   - Adds scalable destination profile helpers for future city, lake, beach,
     corridor, district, park, island, mountain, and region entities.
   - Adds richer Destination DNA including vibe, best-for audiences, featured
     experiences, scan presets, confidence, and time-aware priorities.
   - Makes the canonical city card read as the primary destination.
   - Gives child districts an Explore Within <City> badge instead of presenting
     every district as a top-level destination.
   - Gives HarborWalk Village, Destin Harbor, and Crystal Beach distinct preview
     language based on their own profiles.
   - Adds featured district metadata and stable district ordering.
   - Carries destination confidence, scan presets, vibe, best-for, and signature
     experiences into destination selection and Scan IQ handoff.
   - Keeps the search menu compact, internally scrollable, and free of empty gaps.
   - Adds Destination Authority IQ for broad city coverage beyond the curated registry.
   - Promotes exact provider city/locality matches above places, streets, addresses, and fuzzy text.
   - Uses whole-token authority so Panama City cannot lose to Panamerican Drive.
   - Adds Panama City, Panama City Beach, Historic Downtown Panama City,
     St. Andrews, Shell Island, and Pier Park as canonical destination entities.
   - Builds provider-backed city authority for any U.S. city even when no curated profile exists.
   - Suppresses unrelated provider tail results after an exact city authority match.
   - Preserves City → Related Destination → District/Landmark → Place ordering.
*/

(function(window,document){
"use strict";

if(window.DriveIQAdventureSearch){return;}

var config=window.DriveIQAdventureConfig||{};
var utils=window.DriveIQAdventureUtils;
var state=window.DriveIQAdventureState;
var events=window.DriveIQAdventureEvents;
var destination=window.DriveIQAdventureDestination;

if(!utils||!state||!events||!destination){
  throw new Error("Search requires utils/state/events/destination.");
}

var debounceTimer=null;
var requestSerial=0;
var activeQuery="";
var results=[];
var panel=null;
var suggestionsLocked=false;
var lockReason="";
var initialized=false;
var inputBound=false;
var SEARCH_PANEL_ID="diqAdventureSearchList";
var STALE_SHARED_PANEL_ID="diqAdventureDestResults";
var activeIntent=null;

var ENTITY_REGISTRY=[
  {
    id:"city:destin-fl",
    name:"Destin",
    canonicalName:"Destin",
    label:"Destin, Florida",
    aliases:[
      "destin","destin florida","destin fl"
    ],
    entityType:"city",
    entityClass:"destination_city",
    city:"Destin",
    state:"Florida",
    country:"United States",
    lat:30.3935,
    lng:-86.4958,
    radiusMeters:30000,
    bounds:{
      west:-86.6300,
      south:30.3400,
      east:-86.3600,
      north:30.5000
    },
    marketLabel:"Destin, Florida",
    submarkets:[
      "Destin Harbor","HarborWalk Village","Crystal Beach",
      "Miramar Beach","Sandestin","Okaloosa Island"
    ],
    description:"Gulf Coast destination city for beaches, seafood, boating, shopping, nightlife, and local experiences.",
    destinationProfile:{
      theme:"Coastal Destination City",
      destinationType:"coastal_city",
      confidence:100,
      vibe:["Relaxed","Family Friendly","Upscale Beach"],
      bestFor:["Weekend Trips","Families","Couples","Beach Days"],
      scanPresets:["food","beaches","live_music","shopping","scenic","local_favorites"],
      featuredExperiences:["HarborWalk Village","Crab Island","Sunset cruises","Deep-sea fishing"],
      primary:["beaches","harbor","seafood","sunset","live_music","boating"],
      secondary:["shopping","nightlife","family_fun","dessert","coffee"],
      signatureExperiences:["Harbor sunsets","Fresh seafood","Dolphin cruises","Deep-sea fishing","Beach days","HarborWalk nightlife"],
      timeOfDay:{
        morning:["coffee","breakfast","beach"],
        afternoon:["boating","shopping","family_fun"],
        evening:["seafood","sunset","waterfront"],
        night:["live_music","nightlife"]
      },
      avoid:["industrial","office","generic services"]
    }
  },
  {
    id:"city:panama-city-fl",
    name:"Panama City",
    canonicalName:"Panama City",
    label:"Panama City, Florida",
    aliases:[
      "panama city","panama city florida","panama city fl"
    ],
    entityType:"city",
    entityClass:"destination_city",
    city:"Panama City",
    state:"Florida",
    country:"United States",
    lat:30.1588,
    lng:-85.6602,
    radiusMeters:30000,
    bounds:{
      west:-85.8400,
      south:30.0400,
      east:-85.4700,
      north:30.3000
    },
    marketLabel:"Panama City, Florida",
    submarkets:[
      "Historic Downtown Panama City","St. Andrews",
      "Panama City Beach","Shell Island","Pier Park"
    ],
    description:"Gulf Coast city for waterfront dining, historic districts, boating, beaches, local food, and family experiences.",
    destinationProfile:{
      theme:"Gulf Coast Destination City",
      destinationType:"coastal_city",
      confidence:100,
      vibe:["Waterfront","Local","Family Friendly"],
      bestFor:["Weekend Trips","Families","Boating","Local Food"],
      scanPresets:["local_food","waterfront","beaches","boating","shopping","scenic"],
      featuredExperiences:[
        "Historic Downtown","St. Andrews","Shell Island",
        "Waterfront dining","Bayfront sunsets"
      ],
      primary:["waterfront","local_food","historic","boating","beaches","scenic"],
      secondary:["shopping","family_fun","coffee","live_music"],
      signatureExperiences:[
        "Historic Downtown","St. Andrews waterfront",
        "Shell Island","Bayfront sunsets","Local seafood"
      ],
      timeOfDay:{
        morning:["coffee","historic","waterfront"],
        afternoon:["boating","beaches","shopping"],
        evening:["local_food","sunset","waterfront"],
        night:["live_music","nightlife"]
      },
      avoid:["industrial","office","generic services"]
    }
  },
  {
    id:"city:panama-city-beach-fl",
    name:"Panama City Beach",
    canonicalName:"Panama City Beach",
    label:"Panama City Beach, Florida",
    aliases:[
      "panama city beach","pcb","panama beach",
      "panama city beach florida","panama city beach fl"
    ],
    entityType:"city",
    entityClass:"destination_city",
    city:"Panama City Beach",
    state:"Florida",
    country:"United States",
    lat:30.1766,
    lng:-85.8055,
    radiusMeters:32000,
    bounds:{
      west:-85.9800,
      south:30.0900,
      east:-85.6200,
      north:30.3100
    },
    marketLabel:"Panama City Beach, Florida",
    parentCity:"Panama City",
    parentEntityId:"city:panama-city-fl",
    submarkets:[
      "Pier Park","Shell Island","St. Andrews State Park",
      "Thomas Drive","Laguna Beach"
    ],
    description:"Beach destination for white-sand beaches, Pier Park, seafood, family fun, nightlife, and Gulf experiences.",
    destinationProfile:{
      theme:"Beach Destination City",
      destinationType:"beach_city",
      confidence:99,
      vibe:["Beach","Family Friendly","Lively"],
      bestFor:["Beach Days","Families","Nightlife","Weekend Trips"],
      scanPresets:["beaches","seafood","family_fun","shopping","live_music","nightlife"],
      featuredExperiences:[
        "Pier Park","Shell Island","St. Andrews State Park",
        "Gulf sunsets","Beachfront dining"
      ],
      primary:["beaches","seafood","family_fun","shopping","sunset","nightlife"],
      secondary:["live_music","boating","coffee","dessert"],
      signatureExperiences:[
        "White-sand beaches","Pier Park","Shell Island",
        "Beachfront seafood","Gulf sunsets"
      ],
      timeOfDay:{
        morning:["coffee","breakfast","beaches"],
        afternoon:["beaches","family_fun","shopping"],
        evening:["seafood","sunset","live_music"],
        night:["nightlife","live_music"]
      },
      avoid:["industrial","office"]
    }
  },
  {
    id:"district:historic-downtown-panama-city",
    name:"Historic Downtown Panama City",
    canonicalName:"Historic Downtown Panama City",
    label:"Historic Downtown / Panama City",
    aliases:[
      "historic downtown panama city","downtown panama city",
      "panama city downtown"
    ],
    entityType:"district",
    entityClass:"historic_district",
    city:"Panama City",
    state:"Florida",
    country:"United States",
    lat:30.1522,
    lng:-85.6607,
    radiusMeters:5000,
    marketLabel:"Historic Downtown Panama City",
    parentCity:"Panama City",
    parentEntityId:"city:panama-city-fl",
    description:"Historic waterfront downtown for local restaurants, shops, events, art, and bayfront walks.",
    destinationProfile:{
      theme:"Historic Waterfront Downtown",
      destinationType:"historic_district",
      confidence:97,
      vibe:["Historic","Walkable","Local"],
      bestFor:["Local Food","Shopping","Events","Waterfront Walks"],
      scanPresets:["local_food","shopping","events","coffee","waterfront","art"],
      featuredExperiences:["Local restaurants","Bayfront walks","Shops","Events"],
      primary:["local_food","historic","shopping","waterfront","events"],
      secondary:["coffee","art","live_music"],
      signatureExperiences:["Local restaurants","Bayfront walks","Historic streets","Community events"],
      timeOfDay:{
        morning:["coffee","historic"],
        afternoon:["shopping","art","waterfront"],
        evening:["local_food","events","sunset"],
        night:["live_music"]
      },
      avoid:["industrial","generic chains"]
    }
  },
  {
    id:"district:st-andrews-panama-city",
    name:"St. Andrews",
    canonicalName:"St. Andrews",
    label:"St. Andrews / Panama City",
    aliases:[
      "st andrews panama city","saint andrews panama city",
      "historic st andrews","st andrews florida"
    ],
    entityType:"district",
    entityClass:"waterfront_district",
    city:"Panama City",
    state:"Florida",
    country:"United States",
    lat:30.1669,
    lng:-85.7039,
    radiusMeters:6500,
    marketLabel:"St. Andrews",
    parentCity:"Panama City",
    parentEntityId:"city:panama-city-fl",
    description:"Historic waterfront district for marinas, local seafood, sunset views, shops, and live music.",
    destinationProfile:{
      theme:"Historic Waterfront District",
      destinationType:"waterfront_district",
      confidence:97,
      vibe:["Waterfront","Local","Relaxed"],
      bestFor:["Seafood","Marinas","Sunsets","Live Music"],
      scanPresets:["seafood","waterfront","marina","sunset","live_music","shopping"],
      featuredExperiences:["St. Andrews Marina","Local seafood","Sunsets","Waterfront walks"],
      primary:["seafood","waterfront","marina","sunset","local_food"],
      secondary:["shopping","live_music","coffee"],
      signatureExperiences:["Marina views","Local seafood","Waterfront sunsets","Historic character"],
      timeOfDay:{
        morning:["coffee","marina"],
        afternoon:["shopping","waterfront","local_food"],
        evening:["seafood","sunset","live_music"],
        night:["live_music"]
      },
      avoid:["industrial","office"]
    }
  },
  {
    id:"destination:shell-island-fl",
    name:"Shell Island",
    canonicalName:"Shell Island",
    label:"Shell Island / Panama City Beach",
    aliases:[
      "shell island","shell island florida",
      "shell island panama city","shell island pcb"
    ],
    entityType:"beach",
    entityClass:"natural_destination",
    city:"Panama City Beach",
    state:"Florida",
    country:"United States",
    lat:30.1182,
    lng:-85.7417,
    radiusMeters:9000,
    marketLabel:"Shell Island",
    parentCity:"Panama City",
    parentEntityId:"city:panama-city-fl",
    description:"Undeveloped barrier island for beaches, boat access, wildlife, snorkeling, and scenic Gulf experiences.",
    destinationProfile:{
      theme:"Natural Beach Destination",
      destinationType:"natural_beach",
      confidence:96,
      vibe:["Natural","Scenic","Outdoor"],
      bestFor:["Boating","Beach Days","Wildlife","Snorkeling"],
      scanPresets:["beaches","boating","wildlife","scenic","snorkeling"],
      featuredExperiences:["Boat access","Undeveloped beaches","Wildlife","Snorkeling"],
      primary:["beaches","boating","wildlife","scenic","snorkeling"],
      secondary:["family_fun"],
      signatureExperiences:["Boat access","Natural beach","Wildlife","Clear-water snorkeling"],
      timeOfDay:{
        morning:["boating","wildlife"],
        afternoon:["beaches","snorkeling"],
        evening:["scenic","sunset"],
        night:[]
      },
      avoid:["nightlife","shopping","office"]
    }
  },
  {
    id:"district:pier-park-pcb",
    name:"Pier Park",
    canonicalName:"Pier Park",
    label:"Pier Park / Panama City Beach",
    aliases:["pier park","pier park pcb","pier park panama city beach"],
    entityType:"district",
    entityClass:"shopping_entertainment_district",
    city:"Panama City Beach",
    state:"Florida",
    country:"United States",
    lat:30.2185,
    lng:-85.8731,
    radiusMeters:5500,
    marketLabel:"Pier Park",
    parentCity:"Panama City",
    parentEntityId:"city:panama-city-fl",
    description:"Shopping and entertainment district for restaurants, family fun, events, nightlife, and Gulf views.",
    destinationProfile:{
      theme:"Shopping and Entertainment District",
      destinationType:"entertainment_district",
      confidence:96,
      vibe:["Lively","Family Friendly","Walkable"],
      bestFor:["Shopping","Dining","Family Fun","Nightlife"],
      scanPresets:["shopping","food","family_fun","events","nightlife","dessert"],
      featuredExperiences:["Shopping","Restaurants","SkyWheel","Events","Nightlife"],
      primary:["shopping","food","family_fun","events","nightlife"],
      secondary:["dessert","live_music","coffee"],
      signatureExperiences:["Shopping","Dining","SkyWheel","Events","Nightlife"],
      timeOfDay:{
        morning:["coffee","shopping"],
        afternoon:["shopping","family_fun","food"],
        evening:["dinner","events","sunset"],
        night:["nightlife","live_music"]
      },
      avoid:["industrial","office"]
    }
  },
  {
    id:"corridor:30a-fl",
    name:"30A",
    canonicalName:"Scenic Highway 30A",
    label:"30A / South Walton",
    aliases:[
      "30a","30 a","highway 30a","hwy 30a","scenic 30a",
      "scenic highway 30a","south walton","30a florida"
    ],
    entityType:"corridor",
    entityClass:"destination_corridor",
    city:"South Walton",
    state:"Florida",
    country:"United States",
    lat:30.3295,
    lng:-86.1652,
    radiusMeters:42000,
    bounds:{
      west:-86.3370,
      south:30.2500,
      east:-85.9950,
      north:30.4200
    },
    marketLabel:"30A / South Walton",
    submarkets:[
      "Dune Allen Beach","Gulf Place","Blue Mountain Beach","Grayton Beach",
      "WaterColor","Seaside","Seagrove Beach","Alys Beach",
      "Rosemary Beach","Inlet Beach"
    ],
    description:"Scenic beach corridor connecting South Walton communities.",
    destinationProfile:{
      theme:"Scenic Coastal Corridor",destinationType:"scenic_corridor",
      confidence:94,
      vibe:["Scenic","Boutique","Relaxed"],
      bestFor:["Couples","Coffee Lovers","Beach Town Hopping","Weekend Trips"],
      scanPresets:["coffee","beach_access","local_food","shopping","scenic","live_music"],
      featuredExperiences:["Seaside","Rosemary Beach","Alys Beach","Grayton Beach"],
      primary:["beach_access","coffee","local_food","boutiques","scenic","live_music"],
      secondary:["art","biking","dessert","family_fun","sunset"],
      signatureExperiences:["Beach-town hopping","Morning coffee","Bike rides","Local boutiques","Farmers markets","Rooftop sunsets"],
      timeOfDay:{morning:["coffee","breakfast","biking"],afternoon:["beach_access","shopping","local_food"],evening:["sunset","dinner","live_music"],night:["cocktails","live_music"]},
      avoid:["industrial","generic chain retail"]
    }
  },
  {
    id:"lake:pickwick",
    name:"Pickwick Lake",
    canonicalName:"Pickwick Lake",
    label:"Pickwick Lake",
    aliases:[
      "pickwick lake","pickwick","pickwick landing",
      "pickwick dam","pickwick lake tennessee"
    ],
    entityType:"lake",
    entityClass:"recreation_market",
    city:"Pickwick Dam",
    state:"Tennessee",
    country:"United States",
    lat:35.0617,
    lng:-88.2306,
    radiusMeters:50000,
    bounds:{
      west:-88.4300,
      south:34.8200,
      east:-87.9000,
      north:35.3000
    },
    marketLabel:"Pickwick Lake Region",
    submarkets:[
      "Pickwick Dam","Pickwick Landing","Counce","Hardin County",
      "Iuka","Eastport","Waterloo"
    ],
    description:"Lake recreation market for marinas, boating, fishing, waterfront food, beaches, and scenic stops.",
    destinationProfile:{
      theme:"Lake Recreation Region",destinationType:"lake_region",
      confidence:95,
      vibe:["Outdoor","Relaxed","Water Recreation"],
      bestFor:["Boaters","Anglers","Families","Weekend Escapes"],
      scanPresets:["marina","boat_ramp","fishing","waterfront_food","state_park","scenic"],
      featuredExperiences:["Pickwick Landing State Park","Pickwick Dam","Marinas","Waterfront dining"],
      primary:["marina","boat_ramp","fishing","waterfront","scenic","state_park","beach_access"],
      secondary:["waterfront_food","camping","cabins","coffee","local_food"],
      signatureExperiences:["Marinas","Boat rentals","Fishing access","Waterfront dining","State parks","Scenic overlooks"],
      timeOfDay:{morning:["fishing","boat_ramp","coffee"],afternoon:["boating","beach_access","state_park"],evening:["waterfront_food","sunset","scenic"],night:["cabins","camping"]},
      avoid:["generic inland restaurants","industrial","office"]
    }
  },
  {
    id:"city:charleston-sc",
    name:"Charleston",
    canonicalName:"Charleston",
    label:"Charleston, South Carolina",
    aliases:[
      "charleston","charleston south carolina","charleston sc",
      "charleston, south carolina","charleston, sc"
    ],
    entityType:"city",
    entityClass:"destination_city",
    city:"Charleston",
    state:"South Carolina",
    country:"United States",
    lat:32.7765,
    lng:-79.9311,
    radiusMeters:30000,
    bounds:{
      west:-80.0800,
      south:32.6900,
      east:-79.7800,
      north:32.9000
    },
    marketLabel:"Charleston, South Carolina",
    submarkets:[
      "Historic Charleston","French Quarter","King Street",
      "Waterfront Park","The Battery","Upper King"
    ],
    description:"Historic coastal destination for local food, architecture, markets, waterfront walks, arts, shopping, and nightlife.",
    destinationProfile:{
      theme:"Historic Coastal Destination City",
      destinationType:"historic_coastal_city",
      confidence:100,
      vibe:["Historic","Walkable","Coastal","Culinary"],
      bestFor:["Weekend Trips","Couples","Food Lovers","History"],
      scanPresets:[
        "local_favorites","food","historic","waterfront",
        "shopping","live_music"
      ],
      featuredExperiences:[
        "Historic Charleston","Charleston City Market","King Street",
        "Waterfront Park","The Battery","French Quarter"
      ],
      primary:[
        "local_favorites","historic","local_food","waterfront",
        "architecture","markets","shopping"
      ],
      secondary:[
        "coffee","live_music","nightlife","art","gardens","scenic"
      ],
      signatureExperiences:[
        "Historic streets","Lowcountry food","Charleston City Market",
        "Waterfront walks","King Street","The Battery"
      ],
      timeOfDay:{
        morning:["coffee","historic","market"],
        afternoon:["shopping","art","waterfront"],
        evening:["local_food","sunset","live_music"],
        night:["nightlife","live_music"]
      },
      avoid:["industrial","office","generic services"]
    }
  },
  {
    id:"district:historic-charleston",
    name:"Historic Charleston",
    canonicalName:"Historic Charleston",
    label:"Historic Charleston / Charleston",
    aliases:[
      "historic charleston","charleston historic district",
      "historic district charleston","downtown charleston"
    ],
    entityType:"district",
    entityClass:"historic_district",
    city:"Charleston",
    state:"South Carolina",
    country:"United States",
    lat:32.7763,
    lng:-79.9309,
    radiusMeters:6500,
    marketLabel:"Historic Charleston",
    parentCity:"Charleston",
    parentEntityId:"city:charleston-sc",
    description:"Walkable historic core for architecture, local restaurants, markets, museums, galleries, and waterfront access.",
    destinationProfile:{
      theme:"Historic Walkable District",
      destinationType:"historic_district",
      confidence:99,
      vibe:["Historic","Walkable","Architectural"],
      bestFor:["History","Local Food","Walking","Photography"],
      scanPresets:["historic","local_food","market","art","waterfront","coffee"],
      featuredExperiences:[
        "Historic homes","Church Street","Rainbow Row",
        "Charleston City Market","Waterfront walks"
      ],
      primary:["historic","architecture","local_food","market","art"],
      secondary:["coffee","waterfront","shopping"],
      signatureExperiences:[
        "Historic streets","Rainbow Row","Local restaurants",
        "Markets","Architecture"
      ],
      timeOfDay:{
        morning:["coffee","historic"],
        afternoon:["art","market","shopping"],
        evening:["local_food","waterfront"],
        night:["live_music"]
      },
      avoid:["industrial","office"]
    }
  },
  {
    id:"district:french-quarter-charleston",
    name:"French Quarter",
    canonicalName:"Charleston French Quarter",
    label:"French Quarter / Charleston",
    aliases:[
      "charleston french quarter","french quarter charleston",
      "french quarter south carolina"
    ],
    entityType:"district",
    entityClass:"historic_arts_district",
    city:"Charleston",
    state:"South Carolina",
    country:"United States",
    lat:32.7782,
    lng:-79.9292,
    radiusMeters:3500,
    marketLabel:"Charleston French Quarter",
    parentCity:"Charleston",
    parentEntityId:"city:charleston-sc",
    description:"Historic arts district for galleries, architecture, churches, restaurants, theaters, and waterfront walks.",
    destinationProfile:{
      theme:"Historic Arts District",
      destinationType:"historic_arts_district",
      confidence:98,
      vibe:["Historic","Artistic","Walkable"],
      bestFor:["Art","History","Dining","Architecture"],
      scanPresets:["art","historic","local_food","theater","waterfront","coffee"],
      featuredExperiences:[
        "Art galleries","Historic churches","Dock Street Theatre",
        "Local restaurants","Waterfront walks"
      ],
      primary:["art","historic","architecture","local_food"],
      secondary:["theater","coffee","waterfront"],
      signatureExperiences:[
        "Galleries","Historic churches","Architecture",
        "Local dining","Waterfront access"
      ],
      timeOfDay:{
        morning:["coffee","historic"],
        afternoon:["art","theater"],
        evening:["local_food","waterfront"],
        night:["theater","live_music"]
      },
      avoid:["industrial","office"]
    }
  },
  {
    id:"district:king-street-charleston",
    name:"King Street",
    canonicalName:"King Street Charleston",
    label:"King Street / Charleston",
    aliases:[
      "king street charleston","charleston king street",
      "upper king charleston","lower king charleston"
    ],
    entityType:"district",
    entityClass:"shopping_entertainment_district",
    city:"Charleston",
    state:"South Carolina",
    country:"United States",
    lat:32.7880,
    lng:-79.9370,
    radiusMeters:5000,
    marketLabel:"King Street",
    parentCity:"Charleston",
    parentEntityId:"city:charleston-sc",
    description:"Walkable shopping, dining, nightlife, coffee, boutiques, and local entertainment corridor.",
    destinationProfile:{
      theme:"Shopping and Dining District",
      destinationType:"shopping_entertainment_district",
      confidence:98,
      vibe:["Walkable","Boutique","Lively"],
      bestFor:["Shopping","Dining","Nightlife","Coffee"],
      scanPresets:["shopping","local_food","coffee","nightlife","live_music","dessert"],
      featuredExperiences:[
        "Local boutiques","Restaurants","Coffee shops",
        "Upper King nightlife","Historic storefronts"
      ],
      primary:["shopping","local_food","coffee","nightlife"],
      secondary:["live_music","dessert","historic"],
      signatureExperiences:[
        "Boutiques","Local dining","Coffee","Nightlife","Historic storefronts"
      ],
      timeOfDay:{
        morning:["coffee","shopping"],
        afternoon:["shopping","local_food"],
        evening:["dinner","live_music"],
        night:["nightlife","live_music"]
      },
      avoid:["industrial","office"]
    }
  },
  {
    id:"destination:waterfront-park-charleston",
    name:"Waterfront Park",
    canonicalName:"Charleston Waterfront Park",
    label:"Waterfront Park / Charleston",
    aliases:[
      "charleston waterfront park","waterfront park charleston",
      "pineapple fountain","pineapple fountain charleston"
    ],
    entityType:"park",
    entityClass:"waterfront_destination",
    city:"Charleston",
    state:"South Carolina",
    country:"United States",
    lat:32.7812,
    lng:-79.9253,
    radiusMeters:2500,
    marketLabel:"Charleston Waterfront Park",
    parentCity:"Charleston",
    parentEntityId:"city:charleston-sc",
    description:"Waterfront destination for harbor views, the Pineapple Fountain, walking, family stops, and scenic sunsets.",
    destinationProfile:{
      theme:"Scenic Waterfront Destination",
      destinationType:"waterfront_park",
      confidence:98,
      vibe:["Scenic","Waterfront","Relaxed"],
      bestFor:["Walking","Families","Photos","Sunsets"],
      scanPresets:["waterfront","scenic","family_fun","historic","coffee"],
      featuredExperiences:[
        "Pineapple Fountain","Harbor views","Waterfront walks","Sunsets"
      ],
      primary:["waterfront","scenic","walking","family_fun"],
      secondary:["historic","coffee"],
      signatureExperiences:[
        "Pineapple Fountain","Harbor views","Waterfront walks","Sunsets"
      ],
      timeOfDay:{
        morning:["walking","coffee"],
        afternoon:["waterfront","family_fun"],
        evening:["sunset","scenic"],
        night:[]
      },
      avoid:["industrial","nightlife"]
    }
  },
  {
    id:"district:broadway-nashville",
    name:"Broadway",
    canonicalName:"Broadway Nashville",
    label:"Broadway / Downtown Nashville",
    aliases:["broadway nashville","nashville broadway","lower broadway"],
    entityType:"district",
    entityClass:"entertainment_district",
    city:"Nashville",
    state:"Tennessee",
    country:"United States",
    lat:36.1612,
    lng:-86.7765,
    radiusMeters:5000,
    marketLabel:"Broadway / Downtown Nashville",
    description:"Entertainment district centered on Lower Broadway."
  },
  {
    id:"district:beale-street",
    name:"Beale Street",
    canonicalName:"Beale Street",
    label:"Beale Street / Downtown Memphis",
    aliases:["beale street","beale","beale st"],
    entityType:"district",
    entityClass:"entertainment_district",
    city:"Memphis",
    state:"Tennessee",
    country:"United States",
    lat:35.1395,
    lng:-90.0521,
    radiusMeters:4500,
    marketLabel:"Beale Street / Downtown Memphis",
    description:"Historic entertainment district known for live blues and nightlife."
  },
  {
    id:"district:harborwalk-destin",
    name:"HarborWalk Village",
    canonicalName:"HarborWalk Village",
    label:"HarborWalk Village / Destin Harbor",
    aliases:["harborwalk","harborwalk village","destin harbor"],
    entityType:"district",
    entityClass:"waterfront_district",
    city:"Destin",
    state:"Florida",
    country:"United States",
    lat:30.3935,
    lng:-86.5127,
    radiusMeters:6000,
    marketLabel:"Destin Harbor",
    parentCity:"Destin",
    parentEntityId:"city:destin-fl",
    description:"Waterfront entertainment and dining district.",
    destinationProfile:{
      theme:"Waterfront Entertainment District",destinationType:"waterfront_district",
      confidence:97,
      vibe:["Lively","Waterfront","Family by Day"],
      bestFor:["Live Music","Nightlife","Families","Harbor Dining"],
      scanPresets:["live_music","seafood","nightlife","shopping","family_fun","waterfront"],
      featuredExperiences:["Harbor dining","Boat excursions","Live entertainment","Nightlife"],
      primary:["live_music","seafood","nightlife","shopping","waterfront"],
      secondary:["family_fun","dessert","boating","sunset"],
      signatureExperiences:["Harbor dining","Live entertainment","Boat excursions","Shopping","Nightlife","Sunset views"],
      timeOfDay:{morning:["coffee","boating"],afternoon:["shopping","family_fun","waterfront"],evening:["seafood","sunset","live_music"],night:["nightlife","live_music"]},
      avoid:["industrial","office"]
    }
  },
  {
    id:"district:destin-harbor",
    name:"Destin Harbor",
    canonicalName:"Destin Harbor",
    label:"Destin Harbor",
    aliases:["destin harbor district","harbor district destin"],
    entityType:"district",
    entityClass:"waterfront_district",
    city:"Destin",
    state:"Florida",
    country:"United States",
    lat:30.3938,
    lng:-86.5055,
    radiusMeters:7500,
    marketLabel:"Destin Harbor",
    parentCity:"Destin",
    parentEntityId:"city:destin-fl",
    description:"Waterfront restaurants, charter boats, sunsets, and live entertainment.",
    destinationProfile:{
      theme:"Working Harbor District",
      destinationType:"harbor_district",
      confidence:97,
      vibe:["Waterfront","Active","Local Harbor"],
      bestFor:["Boating","Seafood","Sunsets","Waterfront Dining"],
      scanPresets:["waterfront","seafood","boating","sunset","live_music"],
      featuredExperiences:["Charter boats","Harbor restaurants","Sunset cruises","Waterfront walks"],
      primary:["waterfront","seafood","boating","sunset","live_music"],
      secondary:["shopping","nightlife","family_fun"],
      signatureExperiences:["Charter boats","Harbor restaurants","Sunset cruises","Waterfront walks"],
      timeOfDay:{
        morning:["boating","coffee"],
        afternoon:["waterfront","family_fun"],
        evening:["seafood","sunset"],
        night:["live_music","nightlife"]
      },
      avoid:["industrial","office"]
    }
  },
  {
    id:"district:crystal-beach-destin",
    name:"Crystal Beach",
    canonicalName:"Crystal Beach",
    label:"Crystal Beach / Destin",
    aliases:["crystal beach destin","destin crystal beach"],
    entityType:"district",
    entityClass:"beach_district",
    city:"Destin",
    state:"Florida",
    country:"United States",
    lat:30.3899,
    lng:-86.4208,
    radiusMeters:6500,
    marketLabel:"Crystal Beach",
    parentCity:"Destin",
    parentEntityId:"city:destin-fl",
    description:"Beach access, casual food, family activities, and scenic Gulf stops.",
    destinationProfile:{
      theme:"Beach Neighborhood",
      destinationType:"beach_district",
      confidence:96,
      vibe:["Relaxed","Family Friendly","Scenic"],
      bestFor:["Families","Beach Days","Casual Dining","Sunset Walks"],
      scanPresets:["beach_access","family_fun","casual_food","scenic","coffee","dessert"],
      featuredExperiences:["Beach access","Family beach days","Casual Gulf dining","Sunset walks"],
      primary:["beach_access","family_fun","scenic","casual_food"],
      secondary:["coffee","dessert","shopping","sunset"],
      signatureExperiences:["Beach access","Family beach days","Casual Gulf dining","Sunset walks"],
      timeOfDay:{
        morning:["coffee","beach_access"],
        afternoon:["family_fun","casual_food","beach_access"],
        evening:["sunset","dinner"],
        night:["dessert"]
      },
      avoid:["industrial","nightclub"]
    }
  },
  {
    id:"district:deep-ellum",
    name:"Deep Ellum",
    canonicalName:"Deep Ellum",
    label:"Deep Ellum / Dallas",
    aliases:["deep ellum"],
    entityType:"district",
    entityClass:"entertainment_district",
    city:"Dallas",
    state:"Texas",
    country:"United States",
    lat:32.7842,
    lng:-96.7845,
    radiusMeters:5000,
    marketLabel:"Deep Ellum",
    description:"Dallas entertainment district known for live music, food, and nightlife."
  },
  {
    id:"district:french-quarter",
    name:"French Quarter",
    canonicalName:"French Quarter",
    label:"French Quarter / New Orleans",
    aliases:["french quarter","vieux carre","vieux carré"],
    entityType:"district",
    entityClass:"historic_district",
    city:"New Orleans",
    state:"Louisiana",
    country:"United States",
    lat:29.9584,
    lng:-90.0644,
    radiusMeters:5500,
    marketLabel:"French Quarter",
    description:"Historic New Orleans district for food, music, nightlife, and culture."
  }
];


var CATEGORY_INTENTS=[
  {
    key:"wedding_venue",
    label:"Wedding Venues",
    terms:["wedding venue","wedding venues","wedding reception venue",
      "wedding reception venues","event venue","event venues",
      "wedding location","wedding locations","places to get married"]
  },
  {
    key:"live_music",
    label:"Live Music",
    terms:[
      "live music","music venue","concert","concerts",
      "blues","jazz","honky tonk","honky-tonk"
    ]
  },
  {
    key:"coffee",
    label:"Coffee",
    terms:[
      "coffee","coffee shop","coffee shops","cafe","cafes",
      "café","cafés","espresso","roastery","roaster"
    ]
  },
  {
    key:"food",
    label:"Food",
    terms:[
      "food","restaurant","restaurants","dinner","lunch",
      "breakfast","brunch","places to eat","eat"
    ]
  },
  {
    key:"pizza",
    label:"Pizza",
    terms:["pizza","pizzeria","pizza place","pizza places"]
  },
  {
    key:"bbq",
    label:"BBQ",
    terms:["bbq","barbecue","barbeque"]
  },
  {
    key:"nightlife",
    label:"Nightlife",
    terms:[
      "nightlife","bar","bars","cocktail","cocktails",
      "club","clubs","rooftop","late night"
    ]
  },
  {
    key:"hidden_gems",
    label:"Hidden Gems",
    terms:[
      "hidden gem","hidden gems","local gem","local gems",
      "off the beaten path"
    ]
  },
  {
    key:"outdoors",
    label:"Outdoors",
    terms:[
      "outdoors","outdoor","park","parks","trail","trails",
      "scenic","waterfall","waterfalls","lake","lakes",
      "beach","beaches"
    ]
  },
  {
    key:"dessert",
    label:"Dessert",
    terms:[
      "dessert","desserts","ice cream","bakery","bakeries",
      "sweet treat","sweet treats"
    ]
  },
  {
    key:"shopping",
    label:"Shopping",
    terms:[
      "shopping","shop","shops","store","stores","retail",
      "boutiques","boutique","mall","malls","shopping mall",
      "shopping center","shopping centre","outlet","outlets",
      "department store","department stores","town center",
      "retail center","retail centre"
    ]
  }
];

var PREFERENCE_INTENTS=[
  {key:"best",terms:["best","top","top rated","highest rated","must try","worth it"]},
  {key:"local",terms:["local","locally owned","independent","not a chain","no chains"]},
  {key:"hidden",terms:["hidden","hidden gem","hidden gems","off the beaten path"]},
  {key:"open_now",terms:["open now","open tonight","open today","open late"]},
  {key:"family_friendly",terms:["family friendly","family-friendly","kids","kid friendly","good for kids"]},
  {key:"date_night",terms:["date night","romantic","couples"]},
  {key:"cheap",terms:["cheap","affordable","budget","inexpensive","not expensive"]},
  {key:"upscale",terms:["upscale","fine dining","luxury","premium"]},
  {key:"quiet",terms:["quiet","relaxed","laid back","low key","not loud"]},
  {key:"lively",terms:["lively","energetic","fun atmosphere","party atmosphere"]},
  {key:"walkable",terms:["walkable","easy to walk","all in one area"]},
  {key:"easy_parking",terms:["easy parking","good parking","plenty of parking"]},
  {key:"scenic",terms:["scenic","sunset","view","views","water view"]},
  {key:"worth_the_drive",terms:["worth the drive","worth driving","drive for"]}
];

var ATTRIBUTE_INTENTS=[
  {key:"waterfront",terms:["waterfront","on the water","by the water","water view","water views","beachfront","bayfront","harbor view","marina view"]},
  {key:"live_music",terms:["live music","live band","band playing","music venue","concert","blues","jazz","honky tonk","honky-tonk"]},
  {key:"patio",terms:["patio","outdoor seating","outside seating","rooftop"]},
  {key:"dancing",terms:["dancing","dance floor"]},
  {key:"family_friendly",terms:["family friendly","family-friendly","kid friendly","good for kids"]},
  {key:"dog_friendly",terms:["dog friendly","pet friendly","dogs allowed"]},
  {key:"walkable",terms:["walkable","easy to walk"]},
  {key:"easy_parking",terms:["easy parking","good parking","plenty of parking"]},
  {key:"sunset",terms:["sunset","sunset view","sunset views"]},
  {key:"scenic",terms:["scenic","great view","great views"]}
];

var CUISINE_INTENTS=[
  {key:"seafood",terms:["seafood","fish restaurant","oysters","shrimp","crab","sushi"]},
  {key:"steak",terms:["steak","steakhouse"]},
  {key:"burgers",terms:["burger","burgers"]},
  {key:"pizza",terms:["pizza","pizzeria"]},
  {key:"bbq",terms:["bbq","barbecue","barbeque"]},
  {key:"mexican",terms:["mexican","tacos","taco"]},
  {key:"italian",terms:["italian","pasta"]},
  {key:"asian",terms:["asian","chinese","thai","vietnamese","korean"]},
  {key:"breakfast",terms:["breakfast","brunch"]},
  {key:"dessert",terms:["dessert","ice cream","bakery","sweet treats"]}
];

var SHOPPING_TYPE_INTENTS=[
  {key:"boutique",terms:["boutique","boutiques"]},
  {key:"outlet",terms:["outlet","outlets","outlet mall","factory outlet","outlet center"]},
  {key:"mall",terms:["mall","malls","shopping mall","shopping center","shopping centre","town center","retail center","retail centre"]},
  {key:"department_store",terms:["department store","department stores"]},
  {key:"district",terms:["shopping district","walkable shopping","shopping area","retail district"]},
  {key:"market",terms:["market","markets","farmers market","flea market"]},
  {key:"antique",terms:["antique","antiques","antique mall"]},
  {key:"local_shops",terms:["local shops","local stores","independent shops"]}
];

var PRODUCT_INTENTS=[
  {key:"gifts",terms:["gift","gifts","souvenir","souvenirs"]},
  {key:"clothing",terms:["clothes","clothing","apparel","fashion"]},
  {key:"beachwear",terms:["beachwear","swimsuit","swimsuits","bathing suit"]},
  {key:"mens_clothing",terms:["men's clothing","mens clothing","men's clothes","mens clothes"]},
  {key:"womens_clothing",terms:["women's clothing","womens clothing","women's clothes","womens clothes"]},
  {key:"home_decor",terms:["home decor","home décor","furniture","interior"]},
  {key:"sporting_goods",terms:["sporting goods","outdoor gear","fishing gear"]},
  {key:"art",terms:["art","gallery","galleries","local art"]}
];

var EXCLUSION_INTENTS=[
  {key:"chain",terms:["not a chain","no chains","avoid chains","local only"]},
  {key:"nightclub",terms:["not a club","no clubs","not a nightclub","no nightclubs"]},
  {key:"loud",terms:["not loud","nothing loud","avoid loud"]},
  {key:"expensive",terms:["not expensive","nothing expensive","avoid expensive"]},
  {key:"crowded",terms:["not crowded","avoid crowds","nothing crowded"]},
  {key:"touristy",terms:["not touristy","avoid tourist traps","no tourist traps"]}
];

function registryEntityKey(value){
  return String(value==null?"":value)
    .toLowerCase()
    .replace(/&/g," and ")
    .replace(/[^a-z0-9]+/g," ")
    .replace(/\s+/g," ")
    .trim();
}

function registryEntityMatch(query){
  var key=registryEntityKey(query);
  var best=null;

  if(!key){return null;}

  ENTITY_REGISTRY.forEach(function(entity){
    var aliases=[entity.name,entity.canonicalName,entity.label]
      .concat(entity.aliases||[]);

    aliases.forEach(function(alias){
      var aliasKey=registryEntityKey(alias);
      var score=0;

      if(!aliasKey){return;}

      if(key===aliasKey){
        score=5000+aliasKey.length;
      }else if(
        key.indexOf(aliasKey)>=0&&
        key.length-aliasKey.length<=12
      ){
        score=3200+aliasKey.length;
      }else if(
        aliasKey.indexOf(key)===0&&
        key.length>=3
      ){
        score=1800+key.length;
      }

      if(score&&(!best||score>best.score)){
        best={
          entity:entity,
          score:score,
          alias:alias
        };
      }
    });
  });

  return best;
}

function cloneDestinationProfile(profile){
  var source=profile&&typeof profile==="object"?profile:{};
  var timeOfDay=source.timeOfDay&&typeof source.timeOfDay==="object"
    ? source.timeOfDay
    : {};

  return {
    theme:clean(source.theme||"Destination"),
    destinationType:clean(source.destinationType||"destination"),
    confidence:Number(source.confidence||0),
    primary:Array.isArray(source.primary)?source.primary.slice():[],
    secondary:Array.isArray(source.secondary)?source.secondary.slice():[],
    vibe:Array.isArray(source.vibe)?source.vibe.slice():[],
    bestFor:Array.isArray(source.bestFor)?source.bestFor.slice():[],
    signatureExperiences:Array.isArray(source.signatureExperiences)
      ? source.signatureExperiences.slice()
      : [],
    featuredExperiences:Array.isArray(source.featuredExperiences)
      ? source.featuredExperiences.slice()
      : [],
    scanPresets:Array.isArray(source.scanPresets)
      ? source.scanPresets.slice()
      : [],
    timeOfDay:{
      morning:Array.isArray(timeOfDay.morning)?timeOfDay.morning.slice():[],
      afternoon:Array.isArray(timeOfDay.afternoon)?timeOfDay.afternoon.slice():[],
      evening:Array.isArray(timeOfDay.evening)?timeOfDay.evening.slice():[],
      night:Array.isArray(timeOfDay.night)?timeOfDay.night.slice():[]
    },
    avoid:Array.isArray(source.avoid)?source.avoid.slice():[]
  };
}

function currentDaypart(){
  var hour=new Date().getHours();
  if(hour<11){return "morning";}
  if(hour<17){return "afternoon";}
  if(hour<21){return "evening";}
  return "night";
}

function profileForEntity(entity){
  return cloneDestinationProfile(entity&&entity.destinationProfile);
}

function profileForItem(item){
  return cloneDestinationProfile(item&&item.destinationProfile);
}

function profilePreviewText(item){
  var profile=profileForItem(item);
  var priorities=(profile.timeOfDay[currentDaypart()]||[])
    .concat(profile.primary||[])
    .filter(function(value,index,array){return value&&array.indexOf(value)===index;})
    .slice(0,5);
  return priorities.map(function(value){return clean(value).replace(/_/g," ");}).join(" • ");
}

function titleCaseProfileValue(value){
  return clean(value)
    .replace(/_/g," ")
    .replace(/\b\w/g,function(letter){
      return letter.toUpperCase();
    });
}

function destinationConfidence(entity){
  if(!entity){return 0;}

  if(entity.entityType==="city"){return 100;}
  if(entity.entityType==="district"){return 97;}
  if(entity.entityType==="lake"){return 95;}
  if(entity.entityType==="corridor"){return 94;}
  if(entity.entityType==="beach"){return 93;}
  if(entity.entityType==="park"){return 92;}
  if(entity.entityType==="region"){return 91;}

  return 88;
}

function scanPresetsForProfile(profile){
  var source=cloneDestinationProfile(profile);
  var presets=source.scanPresets.slice();

  if(!presets.length){
    presets=source.primary
      .concat(source.secondary)
      .filter(function(value,index,array){
        return value&&array.indexOf(value)===index;
      })
      .slice(0,6);
  }

  return presets.map(titleCaseProfileValue);
}

function districtPreviewText(item){
  var profile=profileForItem(item);
  var signature=profile.signatureExperiences
    .concat(profile.primary)
    .filter(function(value,index,array){
      return value&&array.indexOf(value)===index;
    })
    .slice(0,5);

  if(signature.length){
    return signature.map(titleCaseProfileValue).join(" • ");
  }

  return "Explore within "+clean(item&&item.parentCity||"the destination");
}

function primaryDestinationSummary(item){
  var profile=profileForItem(item);
  var signature=profile.signatureExperiences
    .concat(profile.primary)
    .filter(function(value,index,array){
      return value&&array.indexOf(value)===index;
    })
    .slice(0,5);

  if(signature.length){
    return signature.map(titleCaseProfileValue).join(" • ");
  }

  return profilePreviewText(item);
}

function exactCanonicalEntityMatch(query){
  var q=registryEntityKey(query);
  var match=null;
  ENTITY_REGISTRY.some(function(entity){
    return [entity.name,entity.canonicalName,entity.label].concat(entity.aliases||[]).some(function(alias){
      if(registryEntityKey(alias)===q){match=entity;return true;}
      return false;
    });
  });
  return match;
}

function entityRegistryItem(entity,query){
  var item={
    placeId:String(entity.id||""),
    id:String(entity.id||""),
    name:clean(entity.canonicalName||entity.name),
    label:clean(entity.label||entity.canonicalName||entity.name),
    full:clean([
      entity.marketLabel,
      entity.state,
      entity.country
    ].filter(Boolean).join(", ")),
    address:clean([
      entity.marketLabel,
      entity.state,
      entity.country
    ].filter(Boolean).join(", ")),
    city:clean(entity.city||""),
    state:clean(entity.state||""),
    country:clean(entity.country||"United States"),
    category:clean(entity.entityType||"destination"),
    entityType:clean(entity.entityType||"destination"),
    entityClass:clean(entity.entityClass||"destination_entity"),
    lat:Number(entity.lat),
    lng:Number(entity.lng),
    radiusMeters:Number(entity.radiusMeters||0),
    explorationRadiusMeters:Number(entity.radiusMeters||0),
    bounds:entity.bounds?Object.assign({},entity.bounds):null,
    marketLabel:clean(entity.marketLabel||entity.label||entity.name),
    submarkets:Array.isArray(entity.submarkets)
      ? entity.submarkets.slice()
      : [],
    aliases:Array.isArray(entity.aliases)
      ? entity.aliases.slice()
      : [],
    description:clean(entity.description||""),
    parentCity:clean(entity.parentCity||""),
    parentEntityId:clean(entity.parentEntityId||""),
    destinationProfile:profileForEntity(entity),
    destinationTheme:clean(entity.destinationProfile&&entity.destinationProfile.theme||""),
    destinationType:clean(
      entity.destinationProfile&&entity.destinationProfile.destinationType||
      entity.entityType||
      "destination"
    ),
    destinationConfidence:Number(
      entity.destinationProfile&&entity.destinationProfile.confidence||
      destinationConfidence(entity)
    ),
    scanPresets:scanPresetsForProfile(entity.destinationProfile),
    canonicalEntity:true,
    destinationEntity:true,
    source:"drive-iq-entity-registry-v600.16",
    intent:activeIntent
      ? Object.assign({},activeIntent)
      : null,
    rawQuery:clean(query||""),
    __score:10000,
    __index:-1
  };

  return item;
}

function exactCityRegistryMatch(query){
  var q=registryEntityKey(query);
  var best=null;

  ENTITY_REGISTRY.forEach(function(entity){
    if(entity.entityType!=="city"){return;}

    [entity.name,entity.canonicalName,entity.label]
      .concat(entity.aliases||[])
      .some(function(alias){
        if(registryEntityKey(alias)===q){
          best=entity;
          return true;
        }
        return false;
      });
  });

  return best;
}

function childDistrictEntities(cityEntity){
  var priority={
    "district:harborwalk-destin":1,
    "district:destin-harbor":2,
    "district:crystal-beach-destin":3,
    "city:panama-city-beach-fl":1,
    "district:historic-downtown-panama-city":2,
    "district:st-andrews-panama-city":3,
    "destination:shell-island-fl":4,
    "district:pier-park-pcb":5,
    "district:historic-charleston":1,
    "district:french-quarter-charleston":2,
    "district:king-street-charleston":3,
    "destination:waterfront-park-charleston":4
  };

  if(!cityEntity){return [];}

  return ENTITY_REGISTRY
    .filter(function(entity){
      return !!(
        entity.id!==cityEntity.id&&
        (
          entity.parentEntityId===cityEntity.id||
          registryEntityKey(entity.parentCity)===
            registryEntityKey(cityEntity.name)
        )
      );
    })
    .sort(function(a,b){
      return Number(priority[a.id]||99)-
        Number(priority[b.id]||99);
    })
    .slice(0,4);
}

function cityClusterResults(query){
  var city=exactCityRegistryMatch(query);
  var output=[];

  if(!city){return output;}

  output.push(entityRegistryItem(city,query));

  childDistrictEntities(city).forEach(function(entity,index){
    var item=entityRegistryItem(entity,query);
    item.__score=9000-(index*50);
    item.__clusterRank=index+1;
    item.cityClusterChild=true;
    output.push(item);
  });

  return output;
}

function registeredEntityResults(query){
  var cluster=cityClusterResults(query);
  var match;

  if(cluster.length){
    return cluster;
  }

  match=registryEntityMatch(query);

  if(!match){return [];}

  return [
    entityRegistryItem(match.entity,query)
  ];
}

function resultIdentityKey(item){
  var lat=Number(item&&item.lat);
  var lng=Number(item&&item.lng);
  var id=clean(item&&(
    item.placeId||
    item.id||
    ""
  ));
  var name=registryEntityKey(item&&(
    item.canonicalName||
    item.name||
    item.label||
    ""
  ));
  var location=registryEntityKey([
    item&&item.city,
    item&&item.state
  ].filter(Boolean).join(" "));

  if(id){return "id:"+id;}

  if(
    name&&
    isFinite(lat)&&
    isFinite(lng)
  ){
    return "geo:"+name+"|"+lat.toFixed(4)+"|"+lng.toFixed(4);
  }

  return "name:"+name+"|"+location;
}

function dedupeResults(list){
  var out=[];
  var seen={};

  (list||[]).forEach(function(item){
    var key=resultIdentityKey(item);

    if(!key||seen[key]){return;}

    seen[key]=true;
    out.push(item);
  });

  return out;
}

function distanceBetweenMeters(a,b){
  var lat1=Number(a&&a.lat);
  var lng1=Number(a&&a.lng);
  var lat2=Number(b&&b.lat);
  var lng2=Number(b&&b.lng);
  var earth=6371000;
  var dLat;
  var dLng;
  var x;
  var y;

  if(
    !isFinite(lat1)||
    !isFinite(lng1)||
    !isFinite(lat2)||
    !isFinite(lng2)
  ){
    return Infinity;
  }

  dLat=(lat2-lat1)*Math.PI/180;
  dLng=(lng2-lng1)*Math.PI/180;
  x=
    Math.sin(dLat/2)*Math.sin(dLat/2)+
    Math.cos(lat1*Math.PI/180)*
    Math.cos(lat2*Math.PI/180)*
    Math.sin(dLng/2)*Math.sin(dLng/2);
  y=2*Math.atan2(Math.sqrt(x),Math.sqrt(1-x));

  return earth*y;
}

function destinationTerms(item){
  return registryEntityKey([
    item&&item.name,
    item&&item.label,
    item&&item.marketLabel,
    item&&item.city,
    item&&item.state
  ].filter(Boolean).join(" "));
}

function suppressIrrelevantCanonicalTail(list,query){
  var exact=exactCanonicalEntityMatch(query);
  var center;
  if(!exact){return list||[];}
  center=entityRegistryItem(exact,query);
  if(exact.entityType==="city"){
    return (list||[]).filter(function(item){
      if(item.canonicalEntity===true){return item.id===exact.id||item.parentEntityId===exact.id;}
      return item.entityType!=="address"&&marketMismatchPenalty(item,query)>-5000;
    }).slice(0,maximumResults());
  }
  return (list||[]).filter(function(item){
    if(item.canonicalEntity===true){return item.id===exact.id||item.parentEntityId===exact.id;}
    return !!(item.entityType!=="address"&&distanceBetweenMeters(item,center)<=Math.max(15000,Number(exact.radiusMeters||0)*0.65)&&(destinationTerms(item).indexOf(registryEntityKey(exact.name))>=0||registryEntityKey(item.city)===registryEntityKey(exact.city)));
  }).slice(0,maximumResults());
}

function suppressCanonicalDuplicates(list){
  var canonical=(list||[]).filter(function(item){
    return item&&item.canonicalEntity===true;
  });

  if(!canonical.length){
    return list||[];
  }

  return (list||[]).filter(function(item){
    var itemTerms;

    if(!item||item.canonicalEntity===true){
      return true;
    }

    itemTerms=destinationTerms(item);

    return !canonical.some(function(entity){
      var entityTerms=destinationTerms(entity);
      var sameName=
        registryEntityKey(item.name)===
        registryEntityKey(entity.name);
      var nearby=
        distanceBetweenMeters(item,entity)<
        Math.max(
          7000,
          Number(entity.radiusMeters||0)*0.35
        );
      var sharedIdentity=
        itemTerms.indexOf(registryEntityKey(entity.name))>=0||
        entityTerms.indexOf(registryEntityKey(item.name))>=0;

      return sameName||(nearby&&sharedIdentity);
    });
  });
}

function entityPreviewText(item){
  var type=clean(item&&item.entityType).toLowerCase();
  var count=Array.isArray(item&&item.submarkets)
    ? item.submarkets.length
    : 0;

  if(type==="district"&&item&&item.parentCity){
    return districtPreviewText(item);
  }

  if(type==="corridor"){
    return count
      ? count+" connected communities • "+primaryDestinationSummary(item)
      : primaryDestinationSummary(item);
  }

  if(type==="lake"){
    return primaryDestinationSummary(item)||
      "Marinas • Boating • Fishing • Waterfront Food • Scenic Stops";
  }

  if(type==="beach"){
    return primaryDestinationSummary(item)||
      "Beaches • Waterfront Food • Scenic Stops • Family Fun";
  }

  if(type==="city"){
    return primaryDestinationSummary(item)||
      "Explore the strongest places and experiences across the city";
  }

  return primaryDestinationSummary(item)||
    clean(item&&item.description||"");
}

function panelModeForCount(count){
  if(count<=2){return "compact";}
  if(count<=5){return "medium";}
  return "scroll";
}

function applyPanelMode(node,count){
  var mode=panelModeForCount(Number(count)||0);

  if(!node){return mode;}

  node.classList.remove(
    "diqSearchCompact",
    "diqSearchMedium",
    "diqSearchScroll"
  );
  node.classList.add(
    mode==="compact"
      ? "diqSearchCompact"
      : mode==="medium"
        ? "diqSearchMedium"
        : "diqSearchScroll"
  );
  node.setAttribute("data-result-count",String(Number(count)||0));

  return mode;
}

function clean(value){
  return String(value==null?"":value).replace(/\s+/g," ").trim();
}

function norm(value){
  return clean(value).toLowerCase();
}

function phrasePattern(value){
  return new RegExp(
    "(^|\\s)"+
    String(value||"")
      .replace(/[.*+?^${}()|[\]\\]/g,"\\$&")
      .replace(/\s+/g,"\\s+")+
    "(?=\\s|$)",
    "i"
  );
}

function removePhrase(value,phrase){
  return clean(
    String(value||"").replace(
      phrasePattern(phrase),
      " "
    )
  );
}

function detectCategories(query){
  var q=norm(query);
  var found=[];

  CATEGORY_INTENTS.forEach(function(category,index){
    var bestTerm="";

    category.terms.forEach(function(term){
      if(
        phrasePattern(norm(term)).test(q)&&
        String(term).length>bestTerm.length
      ){
        bestTerm=term;
      }
    });

    if(bestTerm){
      found.push({
        key:category.key,
        label:category.label,
        term:bestTerm,
        index:index,
        score:String(bestTerm).length
      });
    }
  });

  found.sort(function(a,b){
    return b.score-a.score||a.index-b.index;
  });

  return found;
}

function preferredPrimaryCategory(matches,query){
  var categories=matches||[];
  var q=norm(query);
  var nightlife=null;
  var liveMusic=null;
  var shopping=null;
  var food=null;

  categories.forEach(function(item){
    if(item.key==="nightlife"){nightlife=item;}
    if(item.key==="live_music"){liveMusic=item;}
    if(item.key==="shopping"){shopping=item;}
    if(item.key==="food"){food=item;}
  });

  /*
    Combined real-world intent authority:
    "nightlife with live music" remains Nightlife first, with Live Music
    preserved as a secondary category and explicit attribute.
  */
  if(nightlife&&liveMusic){
    return nightlife;
  }

  if(shopping){
    return shopping;
  }

  if(food){
    return food;
  }

  return categories.length?categories[0]:null;
}

function detectCategory(query){
  return preferredPrimaryCategory(
    detectCategories(query),
    query
  );
}

function detectIntentKeys(query,definitions){
  var q=norm(query);
  var found=[];

  (definitions||[]).forEach(function(definition){
    var matched=(definition.terms||[]).some(function(term){
      return phrasePattern(norm(term)).test(q);
    });

    if(matched&&found.indexOf(definition.key)<0){
      found.push(definition.key);
    }
  });

  return found;
}

function detectPreferences(query){
  return detectIntentKeys(query,PREFERENCE_INTENTS);
}

function detectedRegistryLocation(query){
  var q=registryEntityKey(query);
  var best=null;

  ENTITY_REGISTRY.forEach(function(entity){
    [entity.name,entity.canonicalName,entity.label]
      .concat(entity.aliases||[])
      .forEach(function(alias){
        var key=registryEntityKey(alias);

        if(
          key&&
          q.indexOf(key)>=0&&
          (!best||key.length>best.key.length)
        ){
          best={
            entity:entity,
            alias:alias,
            key:key
          };
        }
      });
  });

  return best;
}

function removeDetectedTerms(value,definitions,keys){
  var output=clean(value);

  (definitions||[]).forEach(function(definition){
    if((keys||[]).indexOf(definition.key)<0){return;}

    (definition.terms||[])
      .slice()
      .sort(function(a,b){
        return String(b).length-String(a).length;
      })
      .forEach(function(term){
        output=removePhrase(output,term);
      });
  });

  return clean(output);
}

function uniqueValues(values){
  return (values||[]).filter(function(value,index,array){
    return value&&array.indexOf(value)===index;
  });
}

function buildRecommendationQuery(intent){
  var parts=[];
  var supplied=intent||{};
  var categoryKey=clean(supplied.category).toLowerCase();
  var shoppingTypes=Array.isArray(supplied.shoppingTypes)
    ? supplied.shoppingTypes.slice()
    : [];

  if(categoryKey==="shopping"){
    /*
     * Generic shopping needs broad recall. A single literal “shopping” query
     * is unreliable in smaller markets, so seed the provider with the main
     * retail families while preserving any specific type the user requested.
     */
    if(!shoppingTypes.length){
      shoppingTypes=[
        "shopping mall",
        "shopping center",
        "outlet",
        "boutique",
        "department store",
        "retail center",
        "market",
        "antique",
        "local shops"
      ];
    }

    parts.push(
      "shopping mall shopping center outlet boutique department store retail center market antique local shops"
    );
  }else if(supplied.categoryLabel){
    parts.push(String(supplied.categoryLabel).toLowerCase());
  }

  (supplied.secondaryCategories||[]).forEach(function(value){
    parts.push(String(value).replace(/_/g," "));
  });
  (supplied.cuisines||[]).forEach(function(value){
    parts.push(String(value).replace(/_/g," "));
  });
  (supplied.attributes||[]).forEach(function(value){
    parts.push(String(value).replace(/_/g," "));
  });
  shoppingTypes.forEach(function(value){
    parts.push(String(value).replace(/_/g," "));
  });
  (supplied.productIntents||[]).forEach(function(value){
    parts.push(String(value).replace(/_/g," "));
  });

  if((supplied.preferences||[]).indexOf("best")>=0){
    parts.push("best top rated");
  }
  if((supplied.preferences||[]).indexOf("local")>=0){
    parts.push("local independent");
  }
  if((supplied.preferences||[]).indexOf("open_now")>=0){
    parts.push("open now");
  }
  if((supplied.preferences||[]).indexOf("cheap")>=0){
    parts.push("affordable");
  }

  return clean(uniqueValues(parts).join(" "));
}

function canonicalCategoryQuery(category){
  if(!category){
    return "";
  }

  /*
    This query is intentionally provider-safe. Location phrases such as
    "near me" are scope instructions, not search vocabulary.
  */
  return clean(
    category.label||
    String(category.key||"").replace(/_/g," ")
  ).toLowerCase();
}

function stripIntentWords(query,category,preferences){
  var output=clean(query);

  if(category&&category.term){
    output=removePhrase(output,category.term);
  }

  PREFERENCE_INTENTS.forEach(function(preference){
    if((preferences||[]).indexOf(preference.key)<0){
      return;
    }

    preference.terms
      .slice()
      .sort(function(a,b){
        return String(b).length-String(a).length;
      })
      .forEach(function(term){
        output=removePhrase(output,term);
      });
  });

  return clean(output);
}

function parseNaturalIntent(query){
  var original=clean(query);
  var working=original;
  var lower=norm(original);
  var exactEntity=exactCanonicalEntityMatch(original);
  var categoryMatches=exactEntity?[]:detectCategories(original);
  var category=preferredPrimaryCategory(categoryMatches,original);
  var secondaryCategories=categoryMatches
    .filter(function(item){
      return !category||item.key!==category.key;
    })
    .map(function(item){return item.key;});
  var preferences=detectPreferences(original);
  var attributes=detectIntentKeys(original,ATTRIBUTE_INTENTS);
  var cuisines=detectIntentKeys(original,CUISINE_INTENTS);
  var shoppingTypes=detectIntentKeys(original,SHOPPING_TYPE_INTENTS);
  var productIntents=detectIntentKeys(original,PRODUCT_INTENTS);
  var exclusions=detectIntentKeys(original,EXCLUSION_INTENTS);

  if(
    category&&
    category.key==="shopping"&&
    !shoppingTypes.length
  ){
    shoppingTypes=[
      "mall",
      "shopping_center",
      "outlet",
      "boutique",
      "department_store",
      "retail_center",
      "market",
      "antique",
      "local_shops"
    ];
  }

  var registryLocation=detectedRegistryLocation(original);
  var registryMatch=exactEntity
    ? {entity:exactEntity,score:10000,alias:original}
    : registryLocation
      ? {entity:registryLocation.entity,score:9000,alias:registryLocation.alias}
      : registryEntityMatch(original);
  var scope="";
  var locationQuery="";
  var locationPhrase="";
  var explicitLocationMatch;
  var nearTerms=[
    "near me","nearby","close by","around me",
    "close to me","near my location"
  ];
  var alongRouteTerms=[
    "along my route","along route","on my route",
    "on the way","ahead of me"
  ];

  nearTerms.some(function(term){
    if(lower.indexOf(term)>=0){
      scope="near_me";
      locationPhrase=term;
      working=removePhrase(working,term);
      return true;
    }
    return false;
  });

  if(!scope){
    alongRouteTerms.some(function(term){
      if(lower.indexOf(term)>=0){
        scope="along_route";
        locationPhrase=term;
        working=removePhrase(working,term);
        return true;
      }
      return false;
    });
  }

  /*
    Canonical destination extraction comes before the generic "in <place>"
    split. This prevents "Panama City with live music" from becoming a fake
    destination string and keeps Panama City separate from Panama City Beach.
  */
  if(!scope&&registryLocation){
    scope="destination";
    locationQuery=clean(
      registryLocation.entity.canonicalName||
      registryLocation.entity.name
    );
    working=removePhrase(working,registryLocation.alias);
  }

  /*
    Generic destination parser for cities and places not yet in the registry.
    Stop before common experience modifiers when they follow the destination.
  */
  if(!scope&&category){
    explicitLocationMatch=working.match(
      /\s+(?:in|at|around|near)\s+(.+?)(?=\s+(?:with|that has|featuring|and has|but|open now|open tonight)\b|$)/i
    );

    if(
      explicitLocationMatch&&
      clean(explicitLocationMatch[1]).length>=2
    ){
      locationQuery=clean(explicitLocationMatch[1]);
      scope="destination";
      working=clean(
        working.replace(explicitLocationMatch[0]," ")
      );
    }
  }

  categoryMatches.forEach(function(match){
    if(match&&match.term){
      working=removePhrase(working,match.term);
    }
  });

  working=removeDetectedTerms(
    working,
    PREFERENCE_INTENTS,
    preferences
  );
  working=removeDetectedTerms(
    working,
    ATTRIBUTE_INTENTS,
    attributes
  );
  working=removeDetectedTerms(
    working,
    CUISINE_INTENTS,
    cuisines
  );
  working=removeDetectedTerms(
    working,
    SHOPPING_TYPE_INTENTS,
    shoppingTypes
  );
  working=removeDetectedTerms(
    working,
    PRODUCT_INTENTS,
    productIntents
  );
  working=removeDetectedTerms(
    working,
    EXCLUSION_INTENTS,
    exclusions
  );

  working=clean(
    working
      .replace(/\b(?:in|at|around|near|with|featuring|that has)\b\s*$/i,"")
      .replace(/\b(?:find|show|looking for|i want|i need|somewhere|places?|best)\b/gi," ")
      .replace(/\s+/g," ")
  );

  /*
    A recognizable category without an explicit destination defaults nearby.
  */
  if(!scope&&category){
    scope="near_me";
  }

  if(!category&&!scope&&!registryMatch){
    return null;
  }

  if(registryMatch&&!category&&!scope){
    scope="destination";
    locationQuery=clean(
      registryMatch.entity.canonicalName||
      registryMatch.entity.name
    );
    working=locationQuery;
  }

  if(
    category&&category.key==="live_music"&&
    secondaryCategories.indexOf("nightlife")>=0
  ){
    category=categoryMatches.filter(function(item){
      return item.key==="nightlife";
    })[0]||category;
    secondaryCategories=uniqueValues(
      ["live_music"].concat(
        secondaryCategories.filter(function(value){
          return value!=="nightlife";
        })
      )
    );
  }

  if(
    secondaryCategories.indexOf("live_music")>=0&&
    attributes.indexOf("live_music")<0
  ){
    attributes.push("live_music");
  }

  var intent={
    rawQuery:original,
    category:category?category.key:"",
    categoryLabel:category?category.label:"",
    secondaryCategories:uniqueValues(secondaryCategories),
    scope:scope||"destination",
    locationQuery:scope==="destination"
      ? clean(locationQuery||working)
      : "",
    searchQuery:"",
    recommendationQuery:"",
    locationPhrase:locationPhrase,
    preferences:uniqueValues(preferences),
    attributes:uniqueValues(attributes),
    cuisines:uniqueValues(cuisines),
    shoppingTypes:uniqueValues(shoppingTypes),
    productIntents:uniqueValues(productIntents),
    exclusions:uniqueValues(exclusions),
    requiredSignals:uniqueValues(
      attributes.concat(cuisines).concat(shoppingTypes)
    ),
    preferredSignals:uniqueValues(preferences.concat(productIntents)),
    pricePreference:
      preferences.indexOf("cheap")>=0
        ? "affordable"
        : preferences.indexOf("upscale")>=0
          ? "upscale"
          : "",
    usesCurrentLocation:scope==="near_me",
    usesActiveRoute:scope==="along_route",
    shouldAutocomplete:scope==="destination",
    canonicalEntityId:registryMatch?clean(registryMatch.entity.id||""):"",
    canonicalEntityType:registryMatch?clean(registryMatch.entity.entityType||""):"",
    destinationProfile:registryMatch?profileForEntity(registryMatch.entity):null,
    source:"natural-intent-search-v600.20"
  };

  intent.recommendationQuery=
    buildRecommendationQuery(intent)||
    canonicalCategoryQuery(category)||
    clean(working);
  intent.searchQuery=intent.recommendationQuery;

  return intent;
}
function saveIntent(intent,reason){
  activeIntent=intent||null;

  try{
    state.merge("search",{
      intent:activeIntent,
      query:activeIntent
        ? activeIntent.rawQuery
        : ""
    },{
      action:String(reason||"search:intent")
    });
  }catch(error){}

  return activeIntent;
}

function intentDisplayName(intent){
  var category=intent&&intent.categoryLabel
    ? intent.categoryLabel
    : "Explore";
  var preference=(intent&&intent.preferences||[])[0]||"";

  if(preference==="best"){
    category="Best "+category;
  }else if(preference==="local"){
    category="Local "+category;
  }else if(preference==="hidden"){
    category="Hidden "+category;
  }

  if(intent&&intent.scope==="near_me"){
    return category+" near me";
  }

  if(intent&&intent.scope==="along_route"){
    return category+" along my route";
  }

  return category;
}

function chooseIntent(intent){
  var resolved=intent||activeIntent;
  var input=document.getElementById("adventureSearch");

  if(!resolved){return false;}

  saveIntent(resolved,"search:intent-chosen");

  if(input){
    input.value=intentDisplayName(resolved);
  }

  lockSuggestions("natural-intent-selected");

  try{
    events.emit("search:intent-chosen",resolved);
    events.emit("search:intent-resolved",resolved);
  }catch(error){}

  return resolved;
}

function renderIntent(intent){
  var node=ensurePanel();
  var button;

  if(!intent||suggestionsLocked){
    return false;
  }

  node.innerHTML="";
  button=document.createElement("button");
  button.type="button";
  button.className="diqV600SearchItem";
  button.setAttribute("data-natural-intent","1");

  button.innerHTML=
    '<span class="diqV600SearchName">'+
      utils.escapeHTML(intentDisplayName(intent))+
    '</span>'+
    '<span class="diqV600SearchMeta">'+
      utils.escapeHTML(
        intent.scope==="near_me"
          ? "Use current location and Recommendation Intelligence"
          : intent.scope==="along_route"
            ? "Search ahead along your active route"
            : "Choose a destination for this recommendation"
      )+
    '</span>'+
    '<span class="diqV600SearchType">'+
      utils.escapeHTML(
        intent.scope==="near_me"
          ? "NEAR ME"
          : intent.scope==="along_route"
            ? "ALONG ROUTE"
            : "RECOMMENDATION"
      )+
    '</span>';

  button.addEventListener("click",function(event){
    event.preventDefault();
    event.stopPropagation();
    chooseIntent(intent);
  },false);

  node.appendChild(button);
  applyPanelMode(node,1);
  showPanel();

  try{
    events.emit("search:intent-results",{
      query:intent.rawQuery,
      intent:intent
    });
  }catch(error){}

  return true;
}


function minimumLength(){
  return Number(
    config.search&&config.search.minimumQueryLength!=null
      ? config.search.minimumQueryLength
      : 2
  );
}

function debounceMs(){
  return Number(
    config.search&&config.search.debounceMs!=null
      ? config.search.debounceMs
      : 180
  );
}

function maximumResults(){
  return Number(
    config.search&&config.search.maximumResults!=null
      ? config.search.maximumResults
      : 8
  );
}

function geoapifyKey(){
  return String(
    config.api&&config.api.geoapifyKey
      ? config.api.geoapifyKey
      : window.DRIVE_IQ_GEOAPIFY_KEY||""
  );
}



function removeStaleSharedPanel(){
  var stale=document.getElementById(STALE_SHARED_PANEL_ID);

  if(
    stale&&
    stale!==panel
  ){
    try{
      stale.parentNode.removeChild(stale);
    }catch(error){}
  }

  return true;
}


function cancelPending(){
  requestSerial+=1;

  if(debounceTimer){
    window.clearTimeout(debounceTimer);
    debounceTimer=null;
  }

  return true;
}

function panelNode(){
  return panel&&panel.parentNode
    ? panel
    : document.getElementById(SEARCH_PANEL_ID);
}

function destroyPanelContent(){
  var node=panelNode();

  if(!node){return false;}

  node.innerHTML="";
  node.classList.remove("on");
  node.classList.remove("ready");
  node.classList.remove("open");
  node.classList.remove("active");
  node.classList.remove("diqSearchCompact");
  node.classList.remove("diqSearchMedium");
  node.classList.remove("diqSearchScroll");
  node.setAttribute("aria-hidden","true");
  node.style.display="none";
  node.style.visibility="hidden";
  node.style.opacity="0";
  node.style.pointerEvents="none";

  return true;
}

function lockSuggestions(reason){
  suggestionsLocked=true;
  lockReason=String(reason||"locked");
  activeQuery="";
  results=[];

  cancelPending();
  destroyPanelContent();

  try{
    events.emit("search:suggestions-locked",{
      reason:lockReason
    });
  }catch(error){}

  return true;
}

function unlockSuggestions(reason){
  suggestionsLocked=false;
  lockReason=String(reason||"user-focus");

  var node=panelNode();
  if(node){
    node.style.removeProperty("display");
    node.style.removeProperty("visibility");
    node.style.removeProperty("opacity");
    node.style.removeProperty("pointer-events");
  }

  try{
    events.emit("search:suggestions-unlocked",{
      reason:lockReason
    });
  }catch(error){}

  return true;
}

function close(){
  cancelPending();
  results=[];
  activeQuery="";
  destroyPanelContent();
  return true;
}

function ensurePanel(){
  if(panel&&panel.parentNode){return panel;}

  removeStaleSharedPanel();
  panel=document.getElementById(SEARCH_PANEL_ID);

  if(!panel){
    panel=document.createElement("div");
    panel.id=SEARCH_PANEL_ID;
    panel.className="diqV600SearchResults";
    panel.setAttribute("role","listbox");
    panel.setAttribute("aria-live","polite");
    panel.setAttribute("aria-hidden","true");
    panel.setAttribute("data-diq-canonical-search-list","1");

    var app=document.getElementById("adventureApp")||document.body;
    app.appendChild(panel);
  }

  if(!document.getElementById("diqV600SearchStyles")){
    var style=document.createElement("style");
    style.id="diqV600SearchStyles";
    style.textContent=
      "#diqAdventureSearchList.diqV600SearchResults{" +
        "display:none;position:fixed;left:16px;right:16px;" +
        "top:clamp(172px,25dvh,232px);bottom:auto;" +
        "z-index:20000;height:auto;max-height:calc(100dvh - 292px);" +
        "overflow-y:auto;overflow-x:hidden;overscroll-behavior:contain;" +
        "-webkit-overflow-scrolling:touch;padding:9px;border-radius:22px;" +
        "background:rgba(3,12,22,.93);border:1px solid rgba(69,255,205,.26);" +
        "box-shadow:0 20px 52px rgba(0,0,0,.54);" +
        "backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);" +
        "scrollbar-width:thin;" +
      "}" +
      "#diqAdventureSearchList.diqSearchCompact{" +
        "max-height:none;overflow:visible;" +
      "}" +
      "#diqAdventureSearchList.diqSearchMedium{" +
        "max-height:min(46dvh,430px);overflow-y:auto;" +
      "}" +
      "#diqAdventureSearchList.diqSearchScroll{" +
        "max-height:calc(100dvh - 292px);overflow-y:auto;" +
      "}" +
      "body.diqKeyboardOpen #diqAdventureSearchList.diqV600SearchResults{" +
        "top:clamp(154px,22dvh,198px);bottom:auto;" +
        "max-height:calc(100dvh - 220px);" +
        "margin-bottom:12px;" +
      "}" +
      "body.diqKeyboardOpen #diqAdventureSearchList.diqSearchCompact{" +
        "max-height:none;overflow:visible;" +
      "}" +
      "#diqAdventureSearchList.diqV600SearchResults.on{" +
        "display:block;animation:diqSearchPanelIn .18s ease both;" +
      "}" +
      "@keyframes diqSearchPanelIn{" +
        "from{opacity:0;transform:translateY(-8px)}" +
        "to{opacity:1;transform:translateY(0)}" +
      "}" +
      ".diqV600SearchItem{" +
        "display:block;width:100%;padding:11px 12px;margin:0 0 6px;" +
        "border:1px solid rgba(255,255,255,.05);border-radius:15px;text-align:left;" +
        "background:rgba(255,255,255,.055);color:#fff;" +
        "font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
      "}" +
      ".diqV600SearchItem:last-child{margin-bottom:0;}" +
      ".diqV600SearchName{display:block;font-size:15px;line-height:1.15;font-weight:900;}" +
      ".diqV600SearchMeta{display:block;margin-top:5px;font-size:11px;line-height:1.25;color:#9fb7c8;}" +
      ".diqV600SearchType{display:inline-block;margin-top:6px;font-size:9px;" +
        "font-weight:900;letter-spacing:.8px;color:#43f4c4;}" +
      ".diqV600SearchEntity{background:linear-gradient(145deg,rgba(255,214,120,.09),rgba(72,241,195,.07));" +
        "border-color:rgba(255,214,120,.22)!important;}" +
      ".diqV600SearchEntity .diqV600SearchType{color:#ffe18a;}" +
      ".diqV600SearchEntityBadge{display:inline-flex;align-items:center;gap:6px;" +
        "margin-bottom:7px;color:#ffe18a;font-size:9px;font-weight:950;" +
        "letter-spacing:.85px;text-transform:uppercase;}" +
      ".diqV600SearchPreview{display:block;margin-top:7px;color:#d5e5ec;" +
        "font-size:10px;line-height:1.35;font-weight:750;}" +
      ".diqV600SearchPrimary{border-color:rgba(255,214,120,.38)!important;" +
        "box-shadow:inset 0 0 0 1px rgba(255,214,120,.08);}" +
      ".diqV600SearchDistrict{background:linear-gradient(145deg," +
        "rgba(72,241,195,.07),rgba(255,255,255,.035));}" +
      ".diqV600SearchEmpty{padding:14px;color:#a8bcc9;font:700 13px system-ui;}";
    document.head.appendChild(style);
  }

  return panel;
}

function showPanel(){
  var node;

  if(suggestionsLocked){
    destroyPanelContent();
    return false;
  }

  node=ensurePanel();
  node.style.removeProperty("display");
  node.style.removeProperty("visibility");
  node.style.removeProperty("opacity");
  node.style.removeProperty("pointer-events");
  node.classList.add("on");
  node.setAttribute("aria-hidden","false");

  return true;
}

function hidePanel(){
  var node=panelNode()||ensurePanel();

  node.classList.remove("on");
  node.setAttribute("aria-hidden","true");

  return true;
}

function classifyFeature(properties){
  var type=norm(
    properties.result_type||
    properties.type||
    properties.category||
    ""
  );

  if(type==="county"||type==="administrative"){return "county";}

  if(type==="city"||type==="town"||type==="village"||type==="municipality"){
    return "city";
  }

  if(type==="district"||type==="suburb"||type==="neighbourhood"||type==="neighborhood"){
    return "district";
  }

  if(type==="amenity"||type==="commercial"||properties.name){
    return "place";
  }

  if(type==="street"||type==="address"||properties.housenumber){
    return "address";
  }

  return type||"place";
}

function entityTypePriority(item){
  var type=clean(item&&item.entityType).toLowerCase();

  if(type==="city"){return 5000;}
  if(type==="county"){return 4800;}
  if(type==="corridor"){return 4200;}
  if(type==="lake"){return 4100;}
  if(type==="district"){return 3200;}
  if(type==="beach"){return 3000;}
  if(type==="place"){return 1800;}
  if(type==="address"){return 900;}

  return 0;
}

function exactDestinationName(item,query){
  var q=registryEntityKey(query);
  var name=registryEntityKey(item&&item.name);
  var label=registryEntityKey(item&&item.label);

  return !!(
    q&&
    (
      name===q||
      label===q||
      label.indexOf(q+" ")===0
    )
  );
}

function marketMismatchPenalty(item,query){
  var q=registryEntityKey(query);
  var city=registryEntityKey(item&&item.city);
  var state=registryEntityKey(item&&item.state);
  var full=registryEntityKey(item&&item.full);
  var penalty=0;

  if(q==="destin"||q==="destin florida"||q==="destin fl"){
    if(
      city&&
      city!=="destin"&&
      city!=="miramar beach"&&
      city!=="fort walton beach"&&
      city!=="okaloosa island"
    ){
      penalty-=3500;
    }

    if(
      state&&
      state!=="florida"
    ){
      penalty-=5000;
    }

    if(
      full.indexOf("punta gorda")>=0||
      full.indexOf("fort lauderdale")>=0
    ){
      penalty-=7000;
    }
  }

  return penalty;
}

function childDistrictForCity(item,query){
  var q=registryEntityKey(query);
  var parent=registryEntityKey(item&&item.parentCity);

  return !!(
    item&&
    item.entityType==="district"&&
    parent&&
    (
      parent===q||
      q.indexOf(parent)===0
    )
  );
}

function tokenWords(value){
  return registryEntityKey(value).split(" ").filter(Boolean);
}

function hasWholeTokenSequence(haystack,needle){
  var h=" "+registryEntityKey(haystack)+" ";
  var n=registryEntityKey(needle);

  if(!n){return false;}

  return h.indexOf(" "+n+" ")>=0;
}

function exactCityProviderMatch(item,query){
  var q=registryEntityKey(query);
  var name=registryEntityKey(item&&item.name);
  var city=registryEntityKey(item&&item.city);
  var type=clean(item&&item.entityType).toLowerCase();

  return !!(
    q&&
    type==="city"&&
    (
      name===q||
      city===q||
      registryEntityKey(item&&item.label)===q
    )
  );
}

function providerCityAuthorityScore(item,query){
  var q=registryEntityKey(query);
  var name=registryEntityKey(item&&item.name);
  var label=registryEntityKey(item&&item.label);
  var full=registryEntityKey(item&&item.full);
  var type=clean(item&&item.entityType).toLowerCase();
  var stateKey=registryEntityKey(item&&item.state);
  var fullLocationKey=registryEntityKey([
    item&&item.city,
    item&&item.state,
    item&&item.country,
    item&&item.full,
    item&&item.label
  ].filter(Boolean).join(" "));
  var score=0;

  if(!q){return 0;}

  /*
    Charleston Authority:
    A bare "Charleston" means the canonical Adventure destination in South
    Carolina. An explicitly state-qualified Charleston query still preserves
    the provider's exact requested city.
  */
  if(q==="charleston"){
    if(
      stateKey==="south carolina"||
      stateKey==="sc"||
      fullLocationKey.indexOf("south carolina")>=0
    ){
      score+=24000;
    }else if(
      stateKey==="west virginia"||
      stateKey==="wv"||
      fullLocationKey.indexOf("west virginia")>=0
    ){
      score-=24000;
    }
  }

  if(type==="county" && (name===q || label===q || hasWholeTokenSequence(full,q))){
    score+=15500;
  }else if(exactCityProviderMatch(item,query)){
    score+=16000;
  }else if(type==="city"&&hasWholeTokenSequence(name,q)){
    score+=9000;
  }else if(type==="city"&&hasWholeTokenSequence(label,q)){
    score+=7500;
  }else if(type==="district"&&hasWholeTokenSequence(name,q)){
    score+=4200;
  }else if(type==="place"&&hasWholeTokenSequence(name,q)){
    score+=900;
  }

  if(name.indexOf(q)===0){score+=700;}
  if(label.indexOf(q)===0){score+=500;}
  if(full.indexOf(q)>=0){score+=150;}

  /*
    Partial substring text such as "Panamerican" must not receive authority
    for a multi-token city query such as "Panama City".
  */
  if(
    q.indexOf(" ")>=0&&
    !hasWholeTokenSequence(name,q)&&
    !hasWholeTokenSequence(label,q)&&
    !hasWholeTokenSequence(full,q)
  ){
    score-=2800;
  }

  return score;
}

function providerCityAuthorityItem(list,query){
  var best=null;

  (list||[]).forEach(function(item){
    var score=providerCityAuthorityScore(item,query);

    if(
      item&&
      (item.entityType==="city"||item.entityType==="county")&&
      score>0&&
      (!best||score>best.score)
    ){
      best={item:item,score:score};
    }
  });

  return best;
}

function canonicalScore(item,query){
  var q=norm(query);
  var name=norm(item.name);
  var label=norm(item.label);
  var score=0;

  if(name===q){score+=1800;}
  else if(name.indexOf(q)===0){score+=900;}
  else if(label.indexOf(q)===0){score+=500;}
  else if(label.indexOf(q)>=0){score+=200;}

  if(item.canonicalEntity===true){score+=5000;}
  score+=entityTypePriority(item);

  if(exactDestinationName(item,query)){
    score+=item.entityType==="city"
      ? 8000
      : 1800;
  }

  if(childDistrictForCity(item,query)){
    score+=2600;
  }

  score+=marketMismatchPenalty(item,query);
  score+=providerCityAuthorityScore(item,query);

  if(q==="destin"||q==="destin florida"||q==="destin fl"){
    if(
      item.canonicalEntity===true&&
      item.entityType==="city"&&
      name==="destin"&&
      norm(item.state)==="florida"
    ){
      score+=10000;
    }

    if(
      item.canonicalEntity===true&&
      item.entityType==="district"&&
      norm(item.parentCity)==="destin"
    ){
      score+=3600;
    }

    if(label.indexOf("destin, florida")>=0){
      score+=2200;
    }
  }

  if(q==="30a"||q==="30a florida"||q==="scenic highway 30a"){
    if(item.canonicalEntity===true&&item.entityType==="corridor"){
      score+=5000;
    }else if(
      norm(item.city).indexOf("fort lauderdale")>=0||
      norm(item.full).indexOf("fort lauderdale")>=0
    ){
      score-=5000;
    }else if(name.indexOf("30a")>=0){
      score-=700;
    }
  }

  if(q==="pickwick lake"||q==="pickwick"){
    if(item.canonicalEntity===true&&item.entityType==="lake"){
      score+=5000;
    }else if(name.indexOf("pickwick")>=0&&item.entityType==="place"){
      score-=350;
    }
  }

  return score;
}

function normalizeFeature(feature,index,query){
  var p=feature&&feature.properties?feature.properties:{};
  var coords=
    feature&&feature.geometry&&feature.geometry.coordinates
      ? feature.geometry.coordinates
      : [];

  if(coords.length<2){return null;}

  var entityType=classifyFeature(p);
  var name=clean(
    p.name||
    p.city||
    p.town||
    p.village||
    p.address_line1||
    p.formatted||
    "Destination"
  );

  var item={
    placeId:String(p.place_id||""),
    id:String(p.place_id||""),
    name:name,
    label:name,
    full:clean(p.formatted||p.address_line2||""),
    address:clean(p.formatted||p.address_line2||""),
    city:clean(p.city||p.town||p.village||""),
    state:clean(p.state||""),
    country:clean(p.country||""),
    category:entityType,
    entityType:entityType,
    lat:Number(coords[1]),
    lng:Number(coords[0]),
    parentCity:"",
    parentEntityId:"",
    providerResultType:clean(p.result_type||p.type||""),
    providerRank:Number(p.rank&&p.rank.confidence||p.rank&&p.rank.importance||0),
    source:"geoapify-search-v600.16",
    intent:activeIntent
      ? Object.assign({},activeIntent)
      : null,
    __index:index
  };

  item.__score=canonicalScore(item,query);
  return item;
}

function render(list){
  var node;

  if(suggestionsLocked){
    destroyPanelContent();
    return false;
  }

  node=ensurePanel();
  node.innerHTML="";

  if(!list.length){
    node.innerHTML='<div class="diqV600SearchEmpty">No destination matches found.</div>';
    applyPanelMode(node,1);
    showPanel();
    return;
  }

  list.forEach(function(item,index){
    var button=document.createElement("button");
    button.type="button";
    button.className="diqV600SearchItem"+
      (
        item.canonicalEntity||item.providerCityAuthority
          ? " diqV600SearchEntity"
          : ""
      )+
      (
        (item.canonicalEntity||item.providerCityAuthority)&&
        item.entityType==="city"
          ? " diqV600SearchPrimary"
          : ""
      )+
      (
        item.canonicalEntity&&item.entityType==="district"
          ? " diqV600SearchDistrict"
          : ""
      );
    button.setAttribute("data-index",String(index));

    var meta=[
      item.marketLabel,
      item.city,
      item.state,
      item.country
    ].filter(function(value,position,array){
      return value&&array.indexOf(value)===position;
    }).join(", ");

    var preview=
      item.canonicalEntity||item.providerCityAuthority
        ? entityPreviewText(item)
        : "";
    var entityBadge=
      (item.canonicalEntity||item.providerCityAuthority)&&
      item.entityType==="city"
        ? "★ PRIMARY DESTINATION"
        : item.canonicalEntity&&item.entityType==="district"&&item.parentCity
          ? "EXPLORE WITHIN "+item.parentCity.toUpperCase()
          : "★ DESTINATION";

    button.innerHTML=
      (
        item.canonicalEntity
          ? '<span class="diqV600SearchEntityBadge">★ DESTINATION</span>'
          : ''
      )+
      '<span class="diqV600SearchName">'+utils.escapeHTML(item.name)+'</span>'+
      '<span class="diqV600SearchMeta">'+utils.escapeHTML(meta||item.address)+'</span>'+
      (
        preview
          ? '<span class="diqV600SearchPreview">'+utils.escapeHTML(preview)+'</span>'
          : ''
      )+
      '<span class="diqV600SearchType">'+utils.escapeHTML(
        item.canonicalEntity||item.providerCityAuthority
          ? (
              item.entityType==="corridor"
                ? "DESTINATION CORRIDOR"
                : item.entityType==="lake"
                  ? "LAKE REGION"
                  : item.entityType.toUpperCase()
            )
          : item.entityType.toUpperCase()
      )+'</span>';

    button.addEventListener("click",function(event){
      event.preventDefault();
      choose(item);
    },false);

    node.appendChild(button);
  });

  applyPanelMode(node,list.length);
  showPanel();
}

function requestURL(query){
  var key=geoapifyKey();
  var params=[
    "text="+encodeURIComponent(query),
    "format=geojson",
    "limit="+encodeURIComponent(String(Math.max(12,maximumResults()*2))),
    "lang=en",
    "filter=countrycode:us",
    "bias=countrycode:us",
    "apiKey="+encodeURIComponent(key)
  ];

  return "https://api.geoapify.com/v1/geocode/autocomplete?"+params.join("&");
}

function promoteProviderCityAuthority(list,query){
  var authority=providerCityAuthorityItem(list,query);
  var q=registryEntityKey(query);
  var exactCanonical=exactCanonicalEntityMatch(query);
  var cityItem;

  if(exactCanonical||!authority){
    return list||[];
  }

  cityItem=authority.item;
  cityItem.providerCityAuthority=true;
  cityItem.destinationEntity=true;
  cityItem.destinationConfidence=98;
  cityItem.destinationTheme="City Destination";
  cityItem.destinationType="city";
  cityItem.marketLabel=clean(
    [cityItem.name,cityItem.state].filter(Boolean).join(", ")
  );
  cityItem.description="Explore the strongest places and experiences across the city.";
  cityItem.__score=Math.max(
    Number(cityItem.__score||0),
    15000+Number(authority.score||0)
  );

  /*
    The provider city becomes the authority result. Keep other results only
    when they belong to the same city/market and are not raw addresses.
  */
  return [cityItem].concat(
    (list||[]).filter(function(item){
      var sameCity;
      var wholeQuery;

      if(item===cityItem){return false;}
      if(item&&item.canonicalEntity===true){return true;}
      if(!item||item.entityType==="address"){return false;}

      sameCity=
        registryEntityKey(item.city)===
        registryEntityKey(cityItem.name);
      wholeQuery=
        hasWholeTokenSequence(item.name,q)||
        hasWholeTokenSequence(item.full,q);

      return sameCity||wholeQuery;
    })
  );
}

function suppressFuzzyCityNoise(list,query){
  var q=registryEntityKey(query);
  var hasCityAuthority=(list||[]).some(function(item){
    return !!(
      item&&
      (
        item.canonicalEntity===true&&item.entityType==="city"||
        item.providerCityAuthority===true
      )&&
      (
        registryEntityKey(item.name)===q||
        registryEntityKey(item.city)===q||
        registryEntityKey(item.label)===q
      )
    );
  });

  if(!hasCityAuthority){return list||[];}

  return (list||[]).filter(function(item){
    var sameMarket;

    if(!item){return false;}
    if(item.canonicalEntity===true||item.providerCityAuthority===true){
      return true;
    }
    if(item.entityType==="address"){return false;}

    sameMarket=
      registryEntityKey(item.city)===q||
      hasWholeTokenSequence(item.full,q);

    return sameMarket;
  });
}

function search(query,options){
  var q=clean(query);
  var serial;
  var key=geoapifyKey();
  // V600.21: explicit provider city lookup from Voice IQ must not turn
  // named cities such as Daytona Beach into the generic Outdoors category.
  // This option is internal to Voice IQ; ordinary user search is unchanged.
  var intent=options&&options.forceDestinationLookup===true
    ? {
        rawQuery:q,
        locationQuery:q,
        scope:"destination",
        shouldAutocomplete:true,
        usesCurrentLocation:false,
        usesActiveRoute:false,
        source:"adventure-voice-city-lookup"
      }
    : parseNaturalIntent(q);
  var providerQuery=q;
  var entityResults=registeredEntityResults(q);

  if(suggestionsLocked){
    destroyPanelContent();
    return Promise.resolve([]);
  }

  activeQuery=q;
  saveIntent(intent,"search:intent-parsed");

  if(
    intent&&
    !intent.shouldAutocomplete
  ){
    cancelPending();
    results=[];
    renderIntent(intent);

    try{
      events.emit("search:intent-resolved",intent);
    }catch(error){}

    return Promise.resolve([]);
  }

  if(
    intent&&
    intent.shouldAutocomplete&&
    intent.locationQuery
  ){
    providerQuery=intent.locationQuery;
  }

  if(providerQuery.length<minimumLength()){
    results=[];
    hidePanel();
    events.emit("search:cleared",{});
    return Promise.resolve([]);
  }

  if(!key){
    if(entityResults.length){
      results=entityResults.slice(0,maximumResults());
      render(results);
      events.emit("search:results",{
        query:q,
        providerQuery:providerQuery,
        intent:intent,
        results:results,
        source:"entity-registry"
      });
      return Promise.resolve(results);
    }

    events.emit("search:failed",{
      query:q,
      message:"Geoapify search key is missing."
    });
    return Promise.resolve([]);
  }

  requestSerial+=1;
  serial=requestSerial;

  events.emit("search:requested",{
    query:q,
    providerQuery:providerQuery,
    intent:intent,
    source:options&&options.source||"user",
    timestamp:Date.now()
  });

  return fetch(requestURL(providerQuery))
    .then(function(response){
      if(!response.ok){
        throw new Error("Destination search returned "+response.status);
      }
      return response.json();
    })
    .then(function(data){
      if(
        suggestionsLocked||
        serial!==requestSerial
      ){
        return [];
      }

      var normalized=entityResults.slice();
      (data&&data.features||[]).forEach(function(feature,index){
        var item=normalizeFeature(feature,index,providerQuery);
        if(item){normalized.push(item);}
      });

      normalized=dedupeResults(normalized);
      normalized=suppressCanonicalDuplicates(normalized);
      normalized=suppressIrrelevantCanonicalTail(normalized,providerQuery);
      normalized=promoteProviderCityAuthority(normalized,providerQuery);
      normalized=suppressFuzzyCityNoise(normalized,providerQuery);

      normalized.sort(function(a,b){
        var aCity=
          (a.canonicalEntity||a.providerCityAuthority)&&
          a.entityType==="city";
        var bCity=
          (b.canonicalEntity||b.providerCityAuthority)&&
          b.entityType==="city";
        var aChild=a.cityClusterChild===true;
        var bChild=b.cityClusterChild===true;

        if(aCity!==bCity){
          return aCity?-1:1;
        }

        if(aChild!==bChild){
          return aChild?-1:1;
        }

        if(aChild&&bChild){
          return Number(a.__clusterRank||99)-
            Number(b.__clusterRank||99);
        }

        if(b.__score!==a.__score){return b.__score-a.__score;}
        return a.__index-b.__index;
      });

      if(suggestionsLocked){
        return [];
      }

      results=normalized.slice(0,maximumResults());
      render(results);

      events.emit("search:results",{
        query:q,
        providerQuery:providerQuery,
        intent:intent,
        results:results
      });

      return results;
    })
    .catch(function(error){
      if(
        suggestionsLocked||
        serial!==requestSerial
      ){
        return [];
      }

      results=[];
      render([]);

      events.emit("search:failed",{
        query:q,
        message:String(
          error&&error.message
            ? error.message
            : error||"Destination search failed"
        )
      });

      return [];
    });
}

function debounce(query,options){
  if(suggestionsLocked){
    cancelPending();
    destroyPanelContent();
    return false;
  }

  if(debounceTimer){
    window.clearTimeout(debounceTimer);
  }

  debounceTimer=window.setTimeout(function(){
    debounceTimer=null;
    search(query,options);
  },debounceMs());
}

function choose(result){
  var normalized=destination.normalize(result);
  var input=document.getElementById("adventureSearch");
  var intent=result&&result.intent
    ? result.intent
    : activeIntent;

  if(!normalized){return false;}

  if(result&&(result.canonicalEntity||result.providerCityAuthority)){
    normalized.canonicalEntity=result.canonicalEntity===true;
    normalized.providerCityAuthority=
      result.providerCityAuthority===true;
    normalized.destinationEntity=true;
    normalized.entityClass=result.entityClass||"destination_entity";
    normalized.marketLabel=result.marketLabel||result.label||result.name;
    normalized.radiusMeters=Number(result.radiusMeters||0);
    normalized.explorationRadiusMeters=Number(
      result.explorationRadiusMeters||
      result.radiusMeters||
      0
    );
    normalized.bounds=result.bounds
      ? Object.assign({},result.bounds)
      : null;
    normalized.submarkets=Array.isArray(result.submarkets)
      ? result.submarkets.slice()
      : [];
    normalized.aliases=Array.isArray(result.aliases)
      ? result.aliases.slice()
      : [];
    normalized.parentCity=clean(result.parentCity||"");
    normalized.parentEntityId=clean(result.parentEntityId||"");
    normalized.cityClusterChild=result.cityClusterChild===true;
    normalized.clusterRank=Number(result.__clusterRank||0);
    normalized.destinationProfile=profileForItem(result);
    normalized.destinationTheme=clean(result.destinationTheme||"");
    normalized.destinationType=clean(
      result.destinationType||
      result.entityType||
      "destination"
    );
    normalized.destinationConfidence=Number(
      result.destinationConfidence||
      normalized.destinationProfile&&normalized.destinationProfile.confidence||
      0
    );
    normalized.scanPresets=Array.isArray(result.scanPresets)
      ? result.scanPresets.slice()
      : scanPresetsForProfile(normalized.destinationProfile);
    normalized.destinationVibe=
      normalized.destinationProfile.vibe.slice();
    normalized.destinationBestFor=
      normalized.destinationProfile.bestFor.slice();
    normalized.signatureExperiences=
      normalized.destinationProfile.signatureExperiences.slice();
    normalized.featuredExperiences=
      normalized.destinationProfile.featuredExperiences.slice();
  }

  if(intent){
    normalized.intent=Object.assign({},intent);
    normalized.recommendationCategory=intent.category||"";
    normalized.recommendationPreferences=
      (intent.preferences||[]).slice();
    saveIntent(intent,"search:destination-with-intent");
  }

  destination.select(normalized,"search");

  if(input){
    input.value=normalized.name||normalized.label||"";
  }

  lockSuggestions("destination-selected");

  events.emit("search:chosen",normalized);

  if(intent){
    events.emit("search:intent-chosen",{
      rawQuery:intent.rawQuery,
      category:intent.category,
      categoryLabel:intent.categoryLabel,
      scope:"destination",
      locationQuery:intent.locationQuery,
      preferences:(intent.preferences||[]).slice(),
      destination:normalized,
      destinationProfile:normalized.destinationProfile||null,
      destinationTheme:normalized.destinationTheme||"",
      destinationType:normalized.destinationType||"",
      destinationConfidence:Number(normalized.destinationConfidence||0),
      scanPresets:Array.isArray(normalized.scanPresets)
        ? normalized.scanPresets.slice()
        : [],
      destinationVibe:Array.isArray(normalized.destinationVibe)
        ? normalized.destinationVibe.slice()
        : [],
      destinationBestFor:Array.isArray(normalized.destinationBestFor)
        ? normalized.destinationBestFor.slice()
        : [],
      signatureExperiences:Array.isArray(normalized.signatureExperiences)
        ? normalized.signatureExperiences.slice()
        : [],
      featuredExperiences:Array.isArray(normalized.featuredExperiences)
        ? normalized.featuredExperiences.slice()
        : [],
      usesCurrentLocation:false,
      usesActiveRoute:false,
      source:intent.source
    });
  }

  return normalized;
}

function clear(){
  unlockSuggestions("search-cleared");
  requestSerial+=1;
  activeQuery="";
  results=[];
  saveIntent(null,"search:cleared");

  if(debounceTimer){
    window.clearTimeout(debounceTimer);
    debounceTimer=null;
  }

  hidePanel();
  events.emit("search:cleared",{});
  return true;
}

function updateKeyboardState(){
  var viewport=window.visualViewport;
  var keyboardOpen=false;

  if(viewport){
    keyboardOpen=
      window.innerHeight-viewport.height>140;
  }else{
    keyboardOpen=!!(
      document.activeElement&&
      document.activeElement.id==="adventureSearch"
    );
  }

  if(document.body){
    document.body.classList.toggle(
      "diqKeyboardOpen",
      keyboardOpen
    );
  }

  return keyboardOpen;
}

function bindKeyboardAwareness(){
  if(window.visualViewport){
    window.visualViewport.addEventListener(
      "resize",
      updateKeyboardState,
      false
    );
    window.visualViewport.addEventListener(
      "scroll",
      updateKeyboardState,
      false
    );
  }

  window.addEventListener(
    "resize",
    updateKeyboardState,
    false
  );

  return true;
}

function bindInput(){
  var input=document.getElementById("adventureSearch");

  if(!input||inputBound){return false;}

  input.addEventListener("focus",function(){
    unlockSuggestions("input-focus");
    window.setTimeout(updateKeyboardState,60);
  },false);

  input.addEventListener("blur",function(){
    window.setTimeout(updateKeyboardState,120);
  },false);

  input.addEventListener("input",function(){
    if(suggestionsLocked){
      unlockSuggestions("input-edit");
    }

    saveIntent(
      parseNaturalIntent(input.value),
      "search:input-edited"
    );
  },false);

  inputBound=true;
  return true;
}

function bindAuthorityEvents(){
  events.on("destination:committed",function(){
      lockSuggestions("destination-committed");
  });

  events.on("scan:started",function(){
      lockSuggestions("scan-started");
  });

  events.on("scan:completed",function(){
      lockSuggestions("scan-completed");
  });

  events.on("results:shelf-shown",function(){
      lockSuggestions("results-shelf-shown");
  });

  events.on("place:opened",function(){
    lockSuggestions("place-opened");
  });

  events.on("destination:cleared",function(){
    unlockSuggestions("destination-cleared");
  });

  events.on("ui:mode-changed",function(event){
    var payload=event&&event.payload?event.payload:{};
    var mode=String(payload.mode||"");

      if(mode==="menu"){
      unlockSuggestions("menu-mode");
      return;
    }

    if(
      mode==="scanning"||
      mode==="map"||
      mode==="browse_results"||
      mode==="place"
    ){
      lockSuggestions("ui-mode-"+mode);
    }
  });
}

function init(){
  if(initialized){
    bindInput();
    return true;
  }

  ensurePanel();
  bindInput();
  bindAuthorityEvents();
  bindKeyboardAwareness();
  updateKeyboardState();
  document.addEventListener("click",function(event){
    var input=document.getElementById("adventureSearch");
    var node=panelNode();

    if(
      node&&
      !node.contains(event.target)&&
      event.target!==input
    ){
      hidePanel();
    }
  },false);

  initialized=true;
  return true;
}

window.DriveIQAdventureSearch={
  init:init,
  search:search,
  debounce:debounce,
  choose:choose,
  chooseIntent:chooseIntent,
  parseIntent:parseNaturalIntent,
  resolveIntent:function(query){
    return parseNaturalIntent(query);
  },
  resolveEntity:function(query){
    var match=registryEntityMatch(query);
    return match
      ? entityRegistryItem(match.entity,query)
      : null;
  },
  entityRegistry:function(){
    return ENTITY_REGISTRY.map(function(entity){
      return Object.assign({},entity,{
        aliases:(entity.aliases||[]).slice(),
        submarkets:(entity.submarkets||[]).slice()
      });
    });
  },
  entityPreviewText:entityPreviewText,
  destinationProfile:function(query){
    var entity=exactCanonicalEntityMatch(query);
    return entity?profileForEntity(entity):null;
  },
  providerCityAuthority:function(query,list){
    var authority=providerCityAuthorityItem(list||results,query);
    return authority
      ? {
          name:authority.item.name,
          city:authority.item.city,
          state:authority.item.state,
          score:authority.score
        }
      : null;
  },
  destinationConfidence:function(query){
    var entity=exactCanonicalEntityMatch(query);
    return entity
      ? Number(
          entity.destinationProfile&&entity.destinationProfile.confidence||
          destinationConfidence(entity)
        )
      : 0;
  },
  scanPresets:function(query){
    var entity=exactCanonicalEntityMatch(query);
    return entity
      ? scanPresetsForProfile(entity.destinationProfile)
      : [];
  },
  currentDaypart:currentDaypart,
  cityClusterResults:cityClusterResults,
  panelModeForCount:panelModeForCount,
  activeIntent:function(){
    return activeIntent
      ? Object.assign({},activeIntent)
      : null;
  },
  clear:clear,
  close:close,
  lockSuggestions:lockSuggestions,
  unlockSuggestions:unlockSuggestions,
  isLocked:function(){return suggestionsLocked;},
  activeQuery:function(){return activeQuery;},
  results:function(){return results.slice();},
  status:function(){
    return {
      build:"V600_16_DESTINATION_AUTHORITY_IQ",
      initialized:initialized,
      suggestionsLocked:suggestionsLocked,
      lockReason:lockReason,
      activeQuery:activeQuery,
      activeIntent:activeIntent
        ? Object.assign({},activeIntent)
        : null,
      resultCount:results.length,
      entityRegistryCount:ENTITY_REGISTRY.length,
      profiledEntityCount:ENTITY_REGISTRY.filter(function(entity){
        return !!entity.destinationProfile;
      }).length,
      activeProfiledResult:
        results.length&&results[0].destinationProfile
          ? {
              theme:results[0].destinationProfile.theme,
              confidence:Number(results[0].destinationConfidence||0),
              scanPresetCount:Array.isArray(results[0].scanPresets)
                ? results[0].scanPresets.length
                : 0
            }
          : null,
      activeCanonicalEntity:
        results.length&&results[0].canonicalEntity
          ? results[0].placeId
          : "",
      activeProviderCityAuthority:
        results.length&&results[0].providerCityAuthority
          ? results[0].name
          : "",
      requestSerial:requestSerial,
      panelID:SEARCH_PANEL_ID,
      panelMode:panelNode()
        ? panelModeForCount(results.length)
        : "closed",
      staleSharedPanelPresent:!!document.getElementById(STALE_SHARED_PANEL_ID),
      panelVisible:!!(
        panelNode()&&
        panelNode().classList.contains("on")
      )
    };
  }
};

window.DRIVE_IQ_ADVENTURE_SEARCH_V500=
  window.DriveIQAdventureSearch;

if(document.readyState==="loading"){
  document.addEventListener("DOMContentLoaded",init,{once:true});
}else{
  init();
}

})(window,document);